/* Product Hero New — client-side behaviors for the Miracle Balm PDP.
 *
 * Custom element host for `[data-phn-root]` (Standard PDP variant of the hero).
 * The Miracle-Balm-specific UI is owned by `<miracle-balm-set-builder>`.
 *
 * Responsibilities:
 *   1. Accordion toggle/collapse.
 *   2. Media main <-> thumbs sync (click a thumbnail to scroll the main swiper to that slide).
 *   3. Standard-view variant picker: swap active swatch, update ATC price / form variant id / main image.
 *   4. "Pair With" quick-add buttons.
 *   5. Liquid Ajax Cart loading state for the standard ATC button.
 *   6. Compare Shades (findation) modal — open/close, tabs, swatch sync, ATC.
 *
 * Findation markup lives inside `<product-hero-new>` (see
 * `sections/product-hero-new.liquid`); queries use `this.querySelector`.
 *
 * All event listeners are scoped to `this` where possible. The two
 * `liquid-ajax-cart` document listeners are cached + removed in
 * `disconnectedCallback` so Theme Editor reloads don't leak handlers.
 *
 * `templates/product.new.json` does not load `section-product.js`, so this file
 * also hoists `window.JR.refreshAjaxCartSections` (same implementation) for
 * cart drawer HTML + count updates after ATC.
 */

/** @see `section-product.js` — must stay in sync for cart drawer revalidation. */
window.JR = window.JR || {};

/** Height animation for PDP accordions — ports live `app/utilities/accordion.js`. */
if (typeof window.JR.toggleProductAccordion !== 'function') {
  const PH_ACCORDION_ANIMATION_MS = 425;
  const PH_ACCORDION_RENDER_DELAY_MS = 25;

  function phAccordionPanel(btn) {
    const panel = btn._phAccordionPanel || btn.nextElementSibling;
    if (
      panel &&
      (panel.classList.contains('ph-accordion__body') || panel.classList.contains('phn-accordion__body'))
    ) {
      btn._phAccordionPanel = panel;
      return panel;
    }
    return null;
  }

  function phAccordionSyncIcon(btn, expanded) {
    const icon = btn.querySelector('.phn-accordion__icon, .ph-accordion__icon');
    if (icon) icon.textContent = expanded ? '−' : '+';
  }

  function phInitProductAccordion(btn) {
    const panel = phAccordionPanel(btn);
    if (!panel) return;
    const isExpanded = btn.getAttribute('aria-expanded') === 'true';
    panel.removeAttribute('hidden');
    panel.setAttribute('aria-hidden', isExpanded ? 'false' : 'true');
    panel.style.height = isExpanded ? 'auto' : '0px';
    phAccordionSyncIcon(btn, isExpanded);
  }

  function phSetProductAccordionExpanded(btn, expanded) {
    const panel = phAccordionPanel(btn);
    if (!panel) return;
    clearTimeout(btn._phAccordionRenderTimeout);
    clearTimeout(btn._phAccordionAnimationTimeout);
    btn.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    panel.removeAttribute('hidden');
    panel.setAttribute('aria-hidden', expanded ? 'false' : 'true');
    panel.style.height = expanded ? 'auto' : '0px';
    phAccordionSyncIcon(btn, expanded);
  }

  function phToggleProductAccordion(btn) {
    const panel = phAccordionPanel(btn);
    if (!panel) return;
    const contentEl = panel.firstElementChild;
    const willExpand = btn.getAttribute('aria-expanded') !== 'true';
    const height = contentEl ? contentEl.scrollHeight : panel.scrollHeight;

    clearTimeout(btn._phAccordionRenderTimeout);
    clearTimeout(btn._phAccordionAnimationTimeout);

    btn.setAttribute('aria-expanded', willExpand ? 'true' : 'false');
    panel.setAttribute('style', willExpand ? 'height: 0px' : `height: ${height}px`);

    btn._phAccordionRenderTimeout = setTimeout(() => {
      panel.setAttribute('style', willExpand ? `height: ${height}px` : 'height: 0px');
      panel.setAttribute('aria-hidden', willExpand ? 'false' : 'true');
    }, PH_ACCORDION_RENDER_DELAY_MS);

    btn._phAccordionAnimationTimeout = setTimeout(() => {
      panel.setAttribute('style', willExpand ? 'height: auto' : 'height: 0px');
    }, PH_ACCORDION_ANIMATION_MS);

    phAccordionSyncIcon(btn, willExpand);
  }

  function phInitProductAccordions(root) {
    const scope = root && root.querySelectorAll ? root : document;
    scope
      .querySelectorAll('[data-ph-accordion-toggle], [data-phn-accordion-toggle], .js-quiz-alt-accordion')
      .forEach((btn) => {
        phInitProductAccordion(btn);
      });
  }

  window.JR.initProductAccordion = phInitProductAccordion;
  window.JR.initProductAccordions = phInitProductAccordions;
  window.JR.toggleProductAccordion = phToggleProductAccordion;
  window.JR.setProductAccordionExpanded = phSetProductAccordionExpanded;
}

/** Ingredients modal — ports live `productIngredients.js` (.open class toggle). */
if (typeof window.JR.initIngredientsModal !== 'function') {
  function phIngredientsModalClick(root, e) {
    const toggle = e.target.closest('.js-ingredientsToggle');
    if (!toggle || !root.contains(toggle)) return;
    const modal = root.querySelector('.js-ingredientsModal');
    if (!modal) return;
    modal.classList.toggle('open');
    modal.setAttribute('aria-hidden', modal.classList.contains('open') ? 'false' : 'true');
  }

  function phInitIngredientsModal(root) {
    if (!root || root._phIngredientsClickHandler) return;
    root._phIngredientsClickHandler = (e) => phIngredientsModalClick(root, e);
    root.addEventListener('click', root._phIngredientsClickHandler);
  }

  function phDestroyIngredientsModal(root) {
    if (!root || !root._phIngredientsClickHandler) return;
    root.removeEventListener('click', root._phIngredientsClickHandler);
    root._phIngredientsClickHandler = null;
  }

  window.JR.initIngredientsModal = phInitIngredientsModal;
  window.JR.destroyIngredientsModal = phDestroyIngredientsModal;
}

if (typeof window.JR.refreshAjaxCartSections !== 'function') {
  window.JR.refreshAjaxCartSections = function refreshAjaxCartSections() {
    document.querySelectorAll('[data-ajax-cart-section]').forEach((cartEl) => {
      const section = cartEl.closest('[id^="shopify-section-"]');
      if (!section) return;
      const sid = section.id.replace('shopify-section-', '');
      fetch(`${window.location.pathname}?sections=${sid}`)
        .then((r) => r.json())
        .then((data) => {
          if (!data[sid]) return;
          const doc = new DOMParser().parseFromString(data[sid], 'text/html');
          const fresh = doc.querySelector('[data-ajax-cart-section]');
          if (fresh) cartEl.innerHTML = fresh.innerHTML;
        })
        .catch(() => {});
    });
  };
}

if (typeof window.JR.refreshCartAndOpen !== 'function') {
  window.JR.refreshCartAndOpen = function refreshCartAndOpen() {
    if (window.liquidAjaxCart && typeof window.liquidAjaxCart.update === 'function') {
      window.liquidAjaxCart.update({}, {});
    }
    if (typeof window.JR.refreshAjaxCartSections === 'function') {
      window.JR.refreshAjaxCartSections();
    }
    window.dispatchEvent(new CustomEvent('cart-open'));
  };
}

function phnPairWithRefreshAndOpenCart() {
  if (typeof window.JR.refreshCartAndOpen === 'function') {
    window.JR.refreshCartAndOpen();
    return;
  }
  if (typeof window.JR.refreshAjaxCartSections === 'function') {
    window.JR.refreshAjaxCartSections();
  }
  window.dispatchEvent(new CustomEvent('cart-open'));
}

function phnMoney(cents) {
  if (cents == null) return '';
  const n = Math.round(Number(cents));
  return `$${(n / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
}

function phnWidthForViewport() {
  if (window.innerWidth > 1000) return 1500;
  if (window.innerWidth > 768) return 1000;
  return 750;
}

function phnFixWidth(url) {
  if (typeof url !== 'string') return url;
  const w = phnWidthForViewport();
  return url.replace(/(\?v=[\w\d]+)&width=\d+\b/g, `$1&width=${w}`);
}

function phnBuildSlideHtml(item, idx) {
  const eager = idx === 0 ? 'fetchpriority="high" loading="eager"' : 'loading="lazy"';
  const src = phnFixWidth(item.src);
  if (item.type === 'video') {
    let sources = (item.sources || [])
      .map((s) => `<source data-src="${s.url}"${s.height ? ` data-h="${s.height}"` : ''} type="${s.mimeType || 'video/mp4'}">`)
      .join('');
    if (!sources && src) sources = `<source data-src="${src}" type="video/mp4">`;
    return (
      '<div class="product__media-slide swiper-slide js-productMediaSlide" data-variant-slide="1">' +
      '<div class="aspect-1-1">' +
      `<video class="aspect-child" width="500" muted loop playsinline preload="none" disablepictureinpicture disableremoteplayback poster="${item.thumb || ''}">` +
      `${sources}</video></div></div>`
    );
  }
  const base = item.srcset || item.src;
  const srcset = `${base} 352w, ${base} 500w, ${base} 832w, ${base} 1200w`;
  return (
    '<div class="product__media-slide swiper-slide js-productMediaSlide" data-variant-slide="1">' +
    '<div class="aspect-1-1">' +
    `<img src="${src}" srcset="${srcset}" width="1500" height="1500" class="aspect-child" ${eager}` +
    ` alt="${(item.alt || '').replace(/"/g, '&quot;')}"></div></div>`
  );
}

const PHN_FINDATION_TRANSITION_MS = 300;

/** True when the shopper is on data-saver or a 2G-class connection — skip any
 *  speculative video prefetch (look-ahead, hover) so we don't spend their bytes. */
function phnIsFrugalConnection() {
  const c = navigator.connection;
  return !!(c && (c.saveData || /(?:^|-)2g\b/.test(c.effectiveType || '')));
}

/**
 * Pick a sensible video rendition height for the size this `<video>` actually
 * renders at (thumb vs. main slide) times the device pixel ratio, then clamp it
 * down on data-saver / slow connections so we never ship a 1080p master to a
 * phone on 3G.
 */
function phnVideoTargetHeight(video) {
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  let h = (video && (video.clientHeight || video.getBoundingClientRect().height)) || 0;
  if (!h) {
    const vw = window.innerWidth || 1024;
    h = vw > 1000 ? 640 : vw > 768 ? 560 : vw;
  }
  const target = Math.round(h * dpr);
  const c = navigator.connection;
  if (c) {
    if (c.saveData || /(?:^|-)2g\b/.test(c.effectiveType || '')) return Math.min(target, 360);
    if (c.effectiveType === '3g') return Math.min(target, 480);
  }
  return target;
}

/**
 * Among a video's `<source data-src>` candidates, keep any HLS source (Safari
 * streams it adaptively) plus the single MP4 rendition closest to the rendered
 * size / connection, and drop the rest — so the browser downloads one
 * right-sized file instead of grabbing the first (often largest) listed.
 * No-op when there's only one source or heights are unknown.
 */
function phnTrimVideoSources(video) {
  const sources = Array.from(video.querySelectorAll('source[data-src]'));
  if (sources.length < 2) return;
  const isHls = (s) =>
    /mpegurl|m3u8/i.test(s.getAttribute('type') || '') ||
    /\.m3u8/i.test(s.getAttribute('data-src') || '');
  const mp4 = sources.filter((s) => !isHls(s));
  const sized = mp4.filter((s) => Number(s.getAttribute('data-h')) > 0);
  if (sized.length < 2) return; // nothing to choose between
  const target = phnVideoTargetHeight(video);
  sized.sort((a, b) => Number(a.getAttribute('data-h')) - Number(b.getAttribute('data-h')));
  const keep = sized.find((s) => Number(s.getAttribute('data-h')) >= target) || sized[sized.length - 1];
  mp4.forEach((s) => {
    if (s !== keep) s.remove();
  });
}

function phnPromoteVideoSources(video, preload = 'auto') {
  if (!video) return false;
  let promoted = false;
  // Trim to the best rendition before the first promote, while data-src markers
  // are still present (runs once — promoted sources have no data-src/data-h).
  if (video.querySelector('source[data-src]')) phnTrimVideoSources(video);
  video.querySelectorAll('source[data-src]').forEach((source) => {
    const src = source.getAttribute('data-src');
    if (!src) return;
    source.setAttribute('src', src);
    source.removeAttribute('data-src');
    source.removeAttribute('data-h');
    promoted = true;
  });
  if (preload) video.preload = preload;
  if (promoted && typeof video.load === 'function') video.load();
  return promoted;
}

function phnHandleize(str) {
  return String(str || '')
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '-');
}

/**
 * Hold a real <img> poster over a gallery video until playback actually starts.
 * iOS WebKit clears the <video> poster and paints a white box while a
 * preload="none" clip buffers after load() — so the shopper sees
 * white → paused frame → motion. A sibling <img> (always rendered on iOS)
 * masks that with the poster, turning it into poster → motion.
 */
function phnShowVideoPoster(video) {
  const posterSrc = video && video.getAttribute('poster');
  const parent = video && video.parentElement;
  if (!posterSrc || !parent) return;
  let overlay = parent.querySelector('.phn-video-poster');
  if (!overlay) {
    overlay = document.createElement('img');
    overlay.className = 'phn-video-poster aspect-child';
    overlay.setAttribute('aria-hidden', 'true');
    overlay.decoding = 'async';
    overlay.src = posterSrc;
    parent.appendChild(overlay);
  }
  overlay.classList.remove('phn-video-poster--hidden');
}

function phnHideVideoPoster(video) {
  const parent = video && video.parentElement;
  const overlay = parent && parent.querySelector('.phn-video-poster');
  if (overlay) overlay.classList.add('phn-video-poster--hidden');
}

function phnPlayMainGalleryVideos(wrapper, activeIndex = 0) {
  const scope = wrapper || document.querySelector('.js-productMedia .swiper-wrapper');
  if (!scope) return;
  const slides = scope.querySelectorAll('.js-productMediaSlide:not([data-phn-loop-clone])');
  slides.forEach((slide, i) => {
    const el = slide.querySelector('video');
    if (!el) return;
    if (i === activeIndex) {
      // Mask the iOS load()/buffer white flash with the poster until playback
      // begins; if frames are already buffered (revisit), drop it immediately.
      if (el.readyState < 3) {
        phnShowVideoPoster(el);
        el.addEventListener('playing', () => phnHideVideoPoster(el), { once: true });
      } else {
        phnHideVideoPoster(el);
      }
      phnPromoteVideoSources(el, 'auto');
      el.muted = true;
      el.controls = false;
      const playVideo = () => {
        const p = el.play?.();
        if (p && typeof p.catch === 'function') p.catch(() => {});
      };
      // iOS requires play() in the user-gesture chain — never defer the first call.
      playVideo();
      if (el.readyState < 3) {
        el.addEventListener('canplay', playVideo, { once: true });
      }
    } else {
      el.pause();
    }
  });
  // Look-ahead: warm the *next upcoming video* (metadata only — moov + first
  // frames) however many image slides sit between here and it, so by the time
  // the shopper swipes to it it starts almost immediately instead of buffering
  // from scratch. Skipped on data-saver / 2G so we don't spend bytes the
  // shopper asked us to conserve. The data-src guard makes this a one-shot per
  // video; once active it's promoted to preload="auto".
  if (!phnIsFrugalConnection()) {
    for (let ahead = activeIndex + 1; ahead < slides.length; ahead += 1) {
      const nextVideo = slides[ahead].querySelector('video');
      if (nextVideo && nextVideo.querySelector('source[data-src]')) {
        phnPromoteVideoSources(nextVideo, 'metadata');
        break;
      }
    }
  }
}

function phnPlayThumbVideo(video, shouldPlay) {
  if (!video) return;
  if (!shouldPlay) {
    video.pause();
    return;
  }
  phnPromoteVideoSources(video, 'auto');
  video.muted = true;
  const playVideo = () => {
    const p = video.play?.();
    if (p && typeof p.catch === 'function') p.catch(() => {});
  };
  playVideo();
  if (video.readyState < 3) video.addEventListener('canplay', playVideo, { once: true });
}

function phnBuildThumbHtml(item, idx) {
  const src = item.thumb || item.src;
  if (item.type === 'video') {
    const sources = (item.sources || [])
      .map((s) => `<source data-src="${s.url}"${s.height ? ` data-h="${s.height}"` : ''} type="${s.mimeType || 'video/mp4'}">`)
      .join('');
    return (
      `<video class="secondary-aspect-child" muted loop playsinline preload="none" disablepictureinpicture disableremoteplayback poster="${item.thumb || ''}">` +
      `${sources}</video>`
    );
  }
  const loading = idx === 0 ? 'eager' : 'lazy';
  return (
    `<img src="${src}" srcset="${item.srcset || item.src} 2x" width="1500" height="1500" class="secondary-aspect-child" loading="${loading}"` +
    ` alt="${(item.alt || '').replace(/"/g, '&quot;')}">`
  );
}

/** Mirror live product-media.js slideChange award clone (lines 455–474). */
function phnCloneHeroAwardsToSlides(root) {
  const scope = root && root.querySelector ? root : document;
  const mobileMainImages = scope.querySelectorAll('.product__media-slide');
  if (mobileMainImages.length === 0) return;

  const awardWrapper = scope.querySelector('.product__hero-award--desktop');
  if (!awardWrapper) return;

  const awardHTML = awardWrapper.outerHTML;
  mobileMainImages.forEach((slide) => {
    const existingAward = slide.querySelector('.product__hero-award--desktop');
    if (existingAward) {
      existingAward.remove();
    }
    slide.insertAdjacentHTML('beforeend', awardHTML);
  });
}

/* Intelligems "PDP Media Gallery" test — read the group client-side and swap
   the main gallery to the custom.ig_pdp_gallery media for the treatment group.
   The test/treatment IDs are emitted per-product on the IG JSON element so a
   single branch can run several product tests at once (WTF, Miracle Balm, …). */

class ProductHeroNew extends HTMLElement {
  /** @type {Object | null} per-instance variant -> media[] cache, populated lazily */
  _variantMediaCache = null;

  /** @type {Object | null} treatment-group variant -> media[] cache (IG test) */
  _igMediaCache = null;

  /** @type {boolean} true when the user is in the IG treatment group */
  _igActive = false;

  /** @type {number | null} hover preview debounce timer */
  _hoverTimer = null;

  /** @type {Function | null} cleanup for thumbnail rail scroll controls */
  _mediaScrollerCleanup = null;

  /** @type {Array<{target: EventTarget, type: string, handler: Function, options?: object}>} */
  _findationListeners = [];

  /** @type {HTMLElement | null} */
  _findationRoot = null;

  /** @type {{ productId: number, variants: Object } | null} */
  _findationData = null;

  /** @type {string | null} */
  _findationSelectedId = null;

  /** @type {HTMLElement | null} toggle that opened the modal (a11y return focus) */
  _findationLastToggle = null;

  /** @type {number | null} */
  _findationCloseTimer = null;

  /** @type {boolean} modal ATC submitted via Compare Shades flow */
  _findationAtcPending = false;

  /** @type {string | null} variant id while a grid cell is hovered (desktop preview) */
  _findationGridHoverIndex = null;

  /** @type {number} previous scrollTop for mini sticky scroll-direction (F8) */
  _stickyPreviousScrollTop = 0;

  /** @type {boolean} tracks body.scrolling-up for desktop mini sticky hide */
  _stickyScrollingUp = false;

  constructor() {
    super();
    this._onAjaxStart = this._onAjaxStart.bind(this);
    this._onAjaxEnd = this._onAjaxEnd.bind(this);
    this._onDelegatedClick = this._onDelegatedClick.bind(this);
    this._onFindationKeydown = this._onFindationKeydown.bind(this);
    this._onFindationMainVariantChange = this._onFindationMainVariantChange.bind(this);
    this._onSectionLoad = this._onSectionLoad.bind(this);
    this._onStickyAtcClick = this._onStickyAtcClick.bind(this);
    this._onStickyShadeToggle = this._onStickyShadeToggle.bind(this);
    this._onStickyShadeItemClick = this._onStickyShadeItemClick.bind(this);
    this._onStickyShadeClose = this._onStickyShadeClose.bind(this);
    this._onStickyShadeDocClick = this._onStickyShadeDocClick.bind(this);
    this._onStickyShadeKeydown = this._onStickyShadeKeydown.bind(this);
    this._onStickyScrollHooks = this._onStickyScrollHooks.bind(this);
    this._onUpsellScrollUnlocked = this._onUpsellScrollUnlocked.bind(this);
    this._onGalleryUpdated = this._onGalleryUpdated.bind(this);
    this._onQuizResultsStickyReset = this._onQuizResultsStickyReset.bind(this);
    this._syncWtfDescriptionAccordion = this._syncWtfDescriptionAccordion.bind(this);
  }

  connectedCallback() {
    this._initIgGallery();
    this._syncWtfDescriptionAccordion();
    window.JR.initProductAccordions(this);
    this._syncWtfDescriptionAccordion();
    this._observeWtfArm();
    this._initWtfReviews();
    window.JR.initIngredientsModal(this);
    this._initMediaSync();
    this._initStandardTabsInitial();
    this._initStandardHoverPreview();
    this._initSizeToggleResync();
    this._syncActiveSwatchGalleriesOnLoad();
    this._initMobilePagination();
    this._initDesktopGalleryDrag();
    this._initThumbScrollSync();
    this._initFindation();
    this._initStickyAtc();
    if (this._isMultiPhnPage()) {
      document.body.classList.add('phn-multi-hero');
      this._bindMultiPhnScrollSync();
    }
    if (document.body.getAttribute('data-template') === 'quiz') {
      document.addEventListener('quiz:results-ready', this._onQuizResultsStickyReset);
    }
    requestAnimationFrame(() => phnCloneHeroAwardsToSlides(this));

    this.addEventListener('click', this._onDelegatedClick);
    document.addEventListener('liquid-ajax-cart:request-start', this._onAjaxStart);
    document.addEventListener('liquid-ajax-cart:request-end', this._onAjaxEnd);
    document.addEventListener('shopify:section:load', this._onSectionLoad);
    document.addEventListener('phn:gallery-updated', this._onGalleryUpdated);

    this.querySelectorAll('[data-phn-videowise-defer]').forEach((embed) => {
      const tpl = embed.parentElement?.querySelector('[data-phn-videowise-html]');
      if (!tpl) return;
      const inject = () => {
        if (embed.dataset.phnVideowiseLoaded) return;
        embed.dataset.phnVideowiseLoaded = 'true';
        embed.innerHTML = tpl.innerHTML;
      };
      if (!('IntersectionObserver' in window)) {
        inject();
        return;
      }
      const io = new IntersectionObserver(
        (entries) => {
          if (entries.some((e) => e.isIntersecting)) {
            inject();
            io.disconnect();
          }
        },
        { rootMargin: '200px' }
      );
      io.observe(embed);
    });
  }

  _onGalleryUpdated() {
    if (!this.isConnected) return;
    requestAnimationFrame(() => {
      this._initMediaSync();
      this._initDesktopGalleryDrag();
      this._initThumbScrollSync();
      if (typeof this._renderMobilePagination === 'function') {
        this._renderMobilePagination();
      }
      phnCloneHeroAwardsToSlides(this);
    });
  }

  disconnectedCallback() {
    window.JR.destroyIngredientsModal(this);
    this._destroyFindation();
    this._destroyStickyAtc();
    this.removeEventListener('click', this._onDelegatedClick);
    document.removeEventListener('liquid-ajax-cart:request-start', this._onAjaxStart);
    document.removeEventListener('liquid-ajax-cart:request-end', this._onAjaxEnd);
    document.removeEventListener('shopify:section:load', this._onSectionLoad);
    document.removeEventListener('phn:gallery-updated', this._onGalleryUpdated);
    document.removeEventListener('quiz:results-ready', this._onQuizResultsStickyReset);
    if (this._hoverTimer) {
      clearTimeout(this._hoverTimer);
      this._hoverTimer = null;
    }
    if (this._hoverPreloadTimer) {
      clearTimeout(this._hoverPreloadTimer);
      this._hoverPreloadTimer = null;
    }
    if (this._phnGalleryObserver) {
      this._phnGalleryObserver.disconnect();
      this._phnGalleryObserver = null;
    }
    if (this._wtfArmObserver) {
      this._wtfArmObserver.disconnect();
      this._wtfArmObserver = null;
    }
    if (this._mediaScrollerCleanup) {
      this._mediaScrollerCleanup();
      this._mediaScrollerCleanup = null;
    }
    if (this._desktopDragCleanup) {
      this._desktopDragCleanup();
      this._desktopDragCleanup = null;
    }
    if (this._thumbScrollSyncCleanup) {
      this._thumbScrollSyncCleanup();
      this._thumbScrollSyncCleanup = null;
    }
    if (this._wtfReviewsCleanup) {
      this._wtfReviewsCleanup();
      this._wtfReviewsCleanup = null;
    }
  }

  _isWtfTestArm() {
    return /^v[1-4]$/i.test(document.documentElement.getAttribute('data-wtf-arm') || '');
  }

  _isWtfPdp() {
    return this.getAttribute('data-product-handle') === 'what-the-foundation';
  }

  _wtfArm() {
    const raw = (document.documentElement.getAttribute('data-wtf-arm') || '').toLowerCase();
    return /^v[1-4]$/.test(raw) ? raw : 'control';
  }

  _trackWtf(kind, extra) {
    if (!this._isWtfPdp()) return;
    const arm = this._wtfArm();
    const allowed = {
      'shade-finder': ['v2', 'v3'],
      'know-shade': ['v4'],
      'take-quiz': ['v4'],
      'review-carousel': ['v3', 'v4'],
      'depth-filter': ['control', 'v1', 'v2', 'v3', 'v4'],
      'shade-selected': ['control', 'v1', 'v2', 'v3', 'v4'],
    };
    if (!(allowed[kind] || []).includes(arm)) return;
    const armLabel = arm === 'control' ? 'Control' : arm.toUpperCase();
    const klaviyoNames = {
      'shade-finder': `WTF ${armLabel} Shade Finder Click`,
      'know-shade': 'WTF V4 Know Your Shade Click',
      'take-quiz': 'WTF V4 Take Quiz Click',
      'review-carousel': `WTF ${armLabel} Review Carousel Click`,
      'depth-filter': `WTF ${armLabel} Depth Filter Click`,
      'shade-selected': `WTF ${armLabel} Shade Selected`,
    };
    const igNames = {
      'shade-finder': `wtf-${arm}-shade-finder-click`,
      'know-shade': 'wtf-v4-know-your-shade-click',
      'take-quiz': 'wtf-v4-take-quiz-click',
      'review-carousel': `wtf-${arm}-review-carousel-click`,
      'depth-filter': `wtf-${arm}-depth-filter-click`,
      'shade-selected': `wtf-${arm}-shade-selected`,
    };
    const props = { Arm: armLabel, Page: location.pathname, ...(extra || {}) };
    window._learnq = window._learnq || [];
    window._learnq.push(['track', klaviyoNames[kind], props]);
    window.igEvents = window.igEvents || [];
    window.igEvents.push({ type: 'track', event: igNames[kind], properties: props });
  }

  _syncWtfDescriptionAccordion() {
    const accordion = this.querySelector('.phn-accordion--description.wtf-desc-force-closed');
    if (!accordion || !this._isWtfPdp() || !this._isWtfTestArm()) return;
    const btn = accordion.querySelector('[data-phn-accordion-toggle]');
    if (btn && typeof window.JR.setProductAccordionExpanded === 'function') {
      window.JR.setProductAccordionExpanded(btn, false);
    }
    accordion.classList.remove('wtf-desc-force-closed');
  }

  _observeWtfArm() {
    if (this._wtfArmObserver) return;
    if (!this._isWtfPdp() || !this.querySelector('.phn-accordion--description.wtf-desc-force-closed')) return;
    this._wtfArmObserver = new MutationObserver(this._syncWtfDescriptionAccordion);
    this._wtfArmObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-wtf-arm'],
    });
  }

  _initWtfReviews() {
    const root = this.querySelector('[data-phn-wtf-reviews]');
    const viewport = root?.querySelector('[data-phn-wtf-reviews-swiper]');
    if (!this._isWtfPdp() || !root || !viewport || root.dataset.phnWtfReviewsReady) return;
    root.dataset.phnWtfReviewsReady = 'true';
    const dots = [...root.querySelectorAll('[data-phn-wtf-reviews-dot]')];
    const prev = root.querySelector('[data-phn-wtf-reviews-prev]');
    const next = root.querySelector('[data-phn-wtf-reviews-next]');
    const syncDots = (index) => {
      dots.forEach((dot, i) => dot.classList.toggle('is-active', i === index));
    };
    this._wtfReviewsBootTries = 0;
    const boot = () => {
      if (!this.isConnected) return;
      if (typeof window.Swiper !== 'function') {
        if (this._wtfReviewsBootTries > 50) return;
        this._wtfReviewsBootTries += 1;
        clearTimeout(this._wtfReviewsBootTimer);
        this._wtfReviewsBootTimer = setTimeout(boot, 80);
        return;
      }
      const arm = (document.documentElement.getAttribute('data-wtf-arm') || '').toLowerCase();
      if (arm !== 'v3' && arm !== 'v4') return;
      if (this._wtfReviewsSwiper) {
        this._wtfReviewsSwiper.update();
        return;
      }
      this._wtfReviewsSwiper = new window.Swiper(viewport, {
        slidesPerView: 1,
        spaceBetween: 0,
        autoHeight: false,
        watchOverflow: true,
        observer: true,
        observeParents: true,
        allowTouchMove: true,
        breakpoints: {
          701: {
            allowTouchMove: false,
          },
        },
        navigation: {
          prevEl: prev,
          nextEl: next,
        },
        on: {
          slideChange: (sw) => {
            syncDots(sw.activeIndex);
            const diff = sw.touches && sw.touches.diff;
            if (diff && Math.abs(diff) > 10) {
              this._trackWtf('review-carousel', { Action: 'swipe', Slide: sw.activeIndex + 1 });
            }
          },
        },
      });
      dots.forEach((dot, i) => {
        dot.addEventListener('click', () => this._wtfReviewsSwiper?.slideTo(i));
      });
    };
    boot();
    const armWatch = new MutationObserver(boot);
    armWatch.observe(document.documentElement, { attributes: true, attributeFilter: ['data-wtf-arm'] });
    this._wtfReviewsCleanup = () => {
      armWatch.disconnect();
      clearTimeout(this._wtfReviewsBootTimer);
      this._wtfReviewsBootTimer = null;
      if (this._wtfReviewsSwiper) {
        this._wtfReviewsSwiper.destroy(true, false);
        this._wtfReviewsSwiper = null;
      }
    };
  }

  _onSectionLoad(evt) {
    const target = evt && evt.target;
    if (!target || typeof target.contains !== 'function') return;
    if (!target.contains(this)) return;
    this._initFindation();
  }

  _getPhnSectionRoot() {
    return this.closest('[data-phn-section]');
  }

  _isMultiPhnPage() {
    return document.querySelectorAll('product-hero-new').length > 1;
  }

  _syncPhnStickyVisibility() {
    const heroes = Array.from(document.querySelectorAll('product-hero-new'));
    let activeHero = null;

    heroes.forEach((hero) => {
      const sentinel = hero.querySelector('.js-stickyAtcObserve');
      if (!sentinel || sentinel.closest('.quiz-pending')) return;
      const rect = sentinel.getBoundingClientRect();
      if (rect.bottom <= window.innerHeight) activeHero = hero;
    });

    document.querySelectorAll('.product__sticky.is-phn-sticky-visible').forEach((sticky) => {
      sticky.classList.remove('is-phn-sticky-visible');
    });

    if (activeHero) {
      heroes.forEach((hero) => {
        if (hero !== activeHero && typeof hero._closeStickyShadeIfOpen === 'function') {
          hero._closeStickyShadeIfOpen();
        }
      });
      activeHero.closest('[data-phn-section]')?.querySelectorAll('.product__sticky').forEach((sticky) => {
        sticky.classList.add('is-phn-sticky-visible');
      });
    }

    document.body.classList.toggle('state--sticky-atc', activeHero !== null);
    if (!activeHero) {
      heroes.forEach((hero) => {
        if (typeof hero._closeStickyShadeIfOpen === 'function') hero._closeStickyShadeIfOpen();
      });
    }
  }

  _bindMultiPhnScrollSync() {
    if (ProductHeroNew._multiPhnScrollBound) return;
    ProductHeroNew._multiPhnScrollBound = true;
    window.addEventListener(
      'scroll',
      () => {
        if (!document.body.classList.contains('phn-multi-hero')) return;
        if (ProductHeroNew._multiPhnScrollRaf) return;
        ProductHeroNew._multiPhnScrollRaf = requestAnimationFrame(() => {
          ProductHeroNew._multiPhnScrollRaf = null;
          document.querySelector('product-hero-new')?._syncPhnStickyVisibility();
        });
      },
      { passive: true }
    );
  }

  /* ── Findation modal (Compare Shades) ───────────────────────────────────── */

  _trackFindationListener(target, type, handler, options) {
    if (!target || !target.addEventListener) return;
    target.addEventListener(type, handler, options);
    this._findationListeners.push({ target, type, handler, options });
  }

  _destroyFindation() {
    if (this._findationCloseTimer) {
      clearTimeout(this._findationCloseTimer);
      this._findationCloseTimer = null;
    }
    this._findationListeners.forEach(({ target, type, handler, options }) => {
      target.removeEventListener(type, handler, options);
    });
    this._findationListeners = [];
    this._findationRoot = null;
    this._findationData = null;
    this._findationSelectedId = null;
    this._findationLastToggle = null;
    this._findationHoverPreviewId = null;
    this._findationAtcPending = false;
    this._findationGridHoverIndex = null;
    if (this._findationAtcEndHandler) {
      document.removeEventListener('liquid-ajax-cart:request-end', this._findationAtcEndHandler);
      this._findationAtcEndHandler = null;
    }
    document.body.classList.remove('phn-findation-open');
  }

  _getFindationRoot() {
    return this.querySelector('[data-phn-findation-root]');
  }

  _isFindationOpen() {
    const root = this._findationRoot;
    return !!(root && !root.hasAttribute('hidden'));
  }

  _initFindation() {
    this._destroyFindation();

    const root = this._getFindationRoot();
    if (!root) return;

    const dataEl = root.querySelector('[data-phn-findation-data]');
    if (!dataEl) return;

    let parsed;
    try {
      parsed = JSON.parse(dataEl.textContent || '{}');
    } catch (err) {
      console.error('[ProductHeroNew] findation JSON parse failed', err);
      return;
    }

    const variants = parsed && parsed.variants;
    const productId = parsed && parsed.productId;
    if (!productId || !variants || typeof variants !== 'object') {
      console.error('[ProductHeroNew] findation JSON missing productId or variants');
      return;
    }
    const variantKeys = Object.keys(variants);
    if (!variantKeys.length) {
      console.error('[ProductHeroNew] findation JSON has no variants');
      return;
    }

    this._findationRoot = root;
    this._findationData = parsed;

    const mainHidden = this.querySelector('[data-phn-std-variant-id]');
    const initialId =
      (mainHidden && mainHidden.value) ||
      String(parsed.selectedVariantId || variantKeys[0]);
    this._findationSelectedId = String(initialId);

    if (mainHidden) {
      this._trackFindationListener(mainHidden, 'change', this._onFindationMainVariantChange);
    }

    this._trackFindationListener(document, 'keydown', this._onFindationKeydown);

    const canHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    if (canHover) {
      const swatchList = root.querySelector('[data-phn-findation-swatches]');
      if (swatchList) {
        const onSwatchListLeave = () => {
          if (this._findationHoverPreviewId == null) return;
          this._findationHoverPreviewId = null;
          if (this._findationSelectedId) {
            const selectedData = this._findationData?.variants?.[this._findationSelectedId];
            const selectedBtn = root.querySelector(
              `[data-phn-findation-swatch][data-variant-id="${this._findationSelectedId}"]`
            );
            this._renderFindationVariantMedia(this._findationSelectedId);
            this._setFindationDescription(selectedData, selectedBtn);
          }
        };
        this._trackFindationListener(swatchList, 'mouseleave', onSwatchListLeave);
      }

      root.querySelectorAll('[data-phn-findation-swatch]').forEach((btn) => {
        const onEnter = () => {
          if (btn.disabled || btn.hidden) return;
          const vid = btn.getAttribute('data-variant-id');
          if (!vid) return;
          const variantData = this._findationData?.variants?.[String(vid)];
          this._findationHoverPreviewId = String(vid);
          this._renderFindationVariantMedia(vid, { preview: true });
          this._setFindationDescription(variantData, btn);
        };
        this._trackFindationListener(btn, 'mouseenter', onEnter);
      });

      const gridCells = root.querySelectorAll('[data-phn-findation-grid-img]');
      gridCells.forEach((cell) => {
        const onEnter = () => {
          const index = Number(cell.getAttribute('data-index'));
          if (!Number.isFinite(index)) return;
          const variantData = this._findationData?.variants?.[this._findationSelectedId];
          const images = variantData?.findation?.images || [];
          const image = images[index];
          if (!image?.src) return;
          this._findationGridHoverIndex = String(index);
          this._setFindationHeroImage(image);
          gridCells.forEach((c) => c.classList.toggle('is-active', c === cell));
        };
        const onLeave = () => {
          if (this._findationGridHoverIndex == null) return;
          this._findationGridHoverIndex = null;
          gridCells.forEach((c) => c.classList.remove('is-active'));
          if (gridCells[0]) gridCells[0].classList.add('is-active');
          if (this._findationHoverPreviewId) {
            this._renderFindationVariantMedia(this._findationHoverPreviewId, { preview: true });
          } else if (this._findationSelectedId) {
            const variantData = this._findationData?.variants?.[this._findationSelectedId];
            const images = variantData?.findation?.images || [];
            this._setFindationHeroImage(images[0] || null);
          }
        };
        this._trackFindationListener(cell, 'mouseenter', onEnter);
        this._trackFindationListener(cell, 'mouseleave', onLeave);
      });
    }
  }

  _onFindationMainVariantChange() {
    const hidden = this.querySelector('[data-phn-std-variant-id]');
    if (!hidden || !hidden.value) return;
    const id = String(hidden.value);
    this._findationSelectedId = id;
    if (this._isFindationOpen()) {
      this._selectFindationVariant(id, { skipMainSync: true });
    }
  }

  _getFindationTabCategory(tab) {
    const title = tab.getAttribute('data-tab-title') || '';
    if (title.trim().toUpperCase() === 'ALL') return 'all';
    return phnHandleize(title);
  }

  _activateFindationTab(tab) {
    const root = this._findationRoot;
    if (!root || !tab) return;
    root.querySelectorAll('[data-phn-findation-tab]').forEach((t) => {
      t.classList.remove('is-active');
      t.setAttribute('aria-selected', 'false');
    });
    tab.classList.add('is-active');
    tab.setAttribute('aria-selected', 'true');
    this._applyFindationCategoryFilter(this._getFindationTabCategory(tab));
  }

  _applyFindationCategoryFilter(categoryHandle) {
    const root = this._findationRoot;
    if (!root) return;
    const swatches = root.querySelectorAll('[data-phn-findation-swatch]');
    swatches.forEach((s) => {
      if (categoryHandle === 'all') {
        s.hidden = false;
        return;
      }
      const cats = (s.getAttribute('data-categories') || '').split(/\s+/).filter(Boolean);
      s.hidden = cats.indexOf(categoryHandle) === -1;
    });

    const active = root.querySelector('[data-phn-findation-swatch].is-active');
    if (!active || active.hidden) {
      const target =
        root.querySelector('[data-phn-findation-swatch]:not([hidden]):not([disabled])') ||
        root.querySelector('[data-phn-findation-swatch]:not([hidden])');
      if (target) this._selectFindationVariant(target.getAttribute('data-variant-id'));
    }
  }

  _syncFindationTabForVariant(variantId) {
    const root = this._findationRoot;
    if (!root || !this._findationData) return;
    const data = this._findationData.variants[String(variantId)];
    const cats = (data?.category || '').split(/\s+/).filter(Boolean);
    const tabs = [...root.querySelectorAll('[data-phn-findation-tab]')];
    let tabToActivate = tabs.find((t) => {
      const cat = this._getFindationTabCategory(t);
      return cat !== 'all' && cats.includes(cat);
    });
    if (!tabToActivate) {
      tabToActivate = tabs.find((t) => this._getFindationTabCategory(t) === 'all') || tabs[0];
    }
    if (tabToActivate) this._activateFindationTab(tabToActivate);
  }

  _setFindationHeroImage(image) {
    const root = this._findationRoot;
    if (!root) return;
    const hero = root.querySelector('[data-phn-findation-hero]');
    if (!hero) return;
    let img = hero.querySelector('[data-phn-findation-hero-img]');
    if (!image || !image.src) {
      if (img) img.remove();
      return;
    }
    if (!img) {
      img = document.createElement('img');
      img.setAttribute('data-phn-findation-hero-img', '');
      img.setAttribute('loading', 'lazy');
      img.setAttribute('width', '650');
      img.setAttribute('height', '650');
      hero.appendChild(img);
    }
    img.src = image.src;
    if (image.srcset) img.srcset = image.srcset;
    else img.removeAttribute('srcset');
    img.alt = image.alt || '';
  }

  _setFindationLookGrid(images) {
    const root = this._findationRoot;
    if (!root) return;
    const cells = root.querySelectorAll('[data-phn-findation-grid-img]');
    cells.forEach((cell, index) => {
      const image = images && images[index];
      let img = cell.querySelector('img');
      if (!image || !image.src) {
        if (img) img.remove();
        return;
      }
      if (!img) {
        img = document.createElement('img');
        img.setAttribute('loading', 'lazy');
        img.setAttribute('width', '200');
        img.setAttribute('height', '200');
        cell.appendChild(img);
      }
      img.src = image.src;
      if (image.srcset) img.srcset = image.srcset;
      else img.removeAttribute('srcset');
      img.alt = image.alt || '';
    });
    cells.forEach((c) => c.classList.remove('is-active'));
    if (cells[0]) cells[0].classList.add('is-active');
  }

  _setFindationDescription(variantData, swatchBtn) {
    const root = this._findationRoot;
    if (!root) return;
    const title =
      (swatchBtn && swatchBtn.getAttribute('data-variant-title')) ||
      variantData?.title ||
      '';
    const subtitleRaw =
      (swatchBtn && swatchBtn.getAttribute('data-variant-subtitle')) ||
      variantData?.findation?.description ||
      '';
    const nameEl = root.querySelector('[data-phn-findation-shade-name]');
    if (nameEl) nameEl.textContent = title;
    const newEl = root.querySelector('[data-phn-findation-shade-new]');
    if (newEl) newEl.hidden = !variantData?.isNew;
    const subEl = root.querySelector('[data-phn-findation-shade-subtitle]');
    if (subEl) {
      const clean = String(subtitleRaw)
        .trim()
        .replace(/^\s*is\s+/i, '');
      if (clean) {
        subEl.textContent = ` is ${clean}`;
        subEl.hidden = false;
      } else {
        subEl.textContent = '';
        subEl.hidden = true;
      }
    }
  }

  _renderFindationVariantMedia(variantId, options = {}) {
    const id = String(variantId);
    const variantData = this._findationData?.variants?.[id];
    if (!variantData) return;
    const images = variantData.findation?.images || [];
    this._setFindationHeroImage(images[0] || null);
    if (!options.preview) {
      this._setFindationLookGrid(images);
    }
  }

  _selectFindationVariant(variantId, options = {}) {
    const root = this._findationRoot;
    if (!root || !variantId) return;
    const id = String(variantId);
    const variantData = this._findationData?.variants?.[id];
    if (!variantData) return;

    this._findationSelectedId = id;

    root.querySelectorAll('[data-phn-findation-swatch]').forEach((b) => {
      b.classList.toggle('is-active', b.getAttribute('data-variant-id') === id);
    });

    const swatchBtn = root.querySelector(`[data-phn-findation-swatch][data-variant-id="${id}"]`);

    this._renderFindationVariantMedia(id);
    this._setFindationDescription(variantData, swatchBtn);

    const priceEl = root.querySelector('[data-phn-findation-atc-price]');
    if (priceEl) {
      const formatted =
        (swatchBtn && swatchBtn.getAttribute('data-variant-price-formatted')) ||
        variantData.priceFormatted ||
        phnMoney(variantData.price);
      if (formatted) priceEl.textContent = formatted;
    }
    this._syncFreeShippingMessage(variantData.price);

    if (!options.skipMainSync) {
      const stdBtn = this.querySelector(`[data-phn-std-shade][data-variant-id="${id}"]`);
      if (stdBtn) {
        this._selectSwatch(stdBtn);
        const hidden = this.querySelector('[data-phn-std-variant-id]');
        if (hidden) {
          hidden.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    }
  }

  _openFindation(toggle) {
    const root = this._findationRoot;
    if (!root) return;

    this._findationLastToggle = toggle || null;

    const mainHidden = this.querySelector('[data-phn-std-variant-id]');
    const mainId = mainHidden?.value;
    if (mainId) this._findationSelectedId = String(mainId);

    root.removeAttribute('hidden');
    root.setAttribute('aria-hidden', 'false');
    root.setAttribute('aria-modal', 'true');
    document.body.classList.add('phn-findation-open');

    const inner = root.querySelector('.phn-findation__inner');
    requestAnimationFrame(() => {
      root.classList.add('is-open');
      if (inner) inner.classList.add('is-open');
    });

    this._syncFindationTabForVariant(this._findationSelectedId);
    const selectedSwatch = root.querySelector(
      `[data-phn-findation-swatch][data-variant-id="${this._findationSelectedId}"]`
    );
    if (selectedSwatch && selectedSwatch.hidden) {
      const allTab = [...root.querySelectorAll('[data-phn-findation-tab]')].find(
        (t) => this._getFindationTabCategory(t) === 'all'
      );
      if (allTab) this._activateFindationTab(allTab);
    }
    this._selectFindationVariant(this._findationSelectedId, { skipMainSync: true });

    const closeBtn = root.querySelector('[data-phn-findation-close]');
    if (closeBtn) closeBtn.focus();
  }

  _closeFindation() {
    const root = this._findationRoot;
    if (!root || root.hasAttribute('hidden')) return;

    const inner = root.querySelector('.phn-findation__inner');
    const toggle = this._findationLastToggle;

    root.classList.remove('is-open');
    if (inner) inner.classList.remove('is-open');
    root.classList.add('is-closing');
    document.body.classList.remove('phn-findation-open');

    const finishClose = () => {
      root.classList.remove('is-closing');
      root.setAttribute('hidden', '');
      root.setAttribute('aria-hidden', 'true');
      root.removeAttribute('aria-modal');
      if (toggle && typeof toggle.focus === 'function') toggle.focus();
    };

    if (this._findationCloseTimer) clearTimeout(this._findationCloseTimer);
    this._findationCloseTimer = setTimeout(finishClose, PHN_FINDATION_TRANSITION_MS);
  }

  _onFindationKeydown(e) {
    if (!this._isFindationOpen()) return;
    const root = this._findationRoot;
    if (!root) return;

    if (e.key === 'Escape') {
      e.preventDefault();
      this._closeFindation();
      return;
    }

    if (e.key !== 'Tab') return;

    const focusable = root.querySelectorAll(
      'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    const list = [...focusable].filter((el) => el.offsetParent !== null && !el.hidden);
    if (!list.length) return;

    const first = list[0];
    const last = list[list.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  _handleFindationTab(tab) {
    this._activateFindationTab(tab);
  }

  _handleFindationSwatch(swatchBtn) {
    const vid = swatchBtn.getAttribute('data-variant-id');
    if (!vid) return;
    this._findationHoverPreviewId = null;
    this._selectFindationVariant(vid);
  }

  _handleFindationAtc(atcBtn) {
    const formId = atcBtn.getAttribute('data-phn-findation-form-id');
    const form = formId ? document.getElementById(formId) : null;
    const submitBtn = form?.querySelector('[data-phn-std-atc]');
    const ajaxWrapper = form?.closest('ajax-cart-product-form');
    if (!form || !submitBtn) return;

    if (this._findationSelectedId) {
      this._selectFindationVariant(this._findationSelectedId);
    }

    if (this._findationAtcEndHandler) {
      document.removeEventListener('liquid-ajax-cart:request-end', this._findationAtcEndHandler);
    }

    this._findationAtcPending = true;
    this._setFindationAtcLoading(atcBtn, true);

    this._findationAtcEndHandler = (e) => {
      if (!this._findationAtcPending) return;
      const rs = e.detail && e.detail.requestState;
      if (!rs || rs.requestType !== 'add') return;
      const initiator = rs.info && rs.info.initiator;
      const fromThisForm =
        initiator === form ||
        initiator === submitBtn ||
        initiator === ajaxWrapper ||
        (initiator &&
          typeof initiator.contains === 'function' &&
          (initiator.contains(form) || initiator.contains(submitBtn)));
      if (!fromThisForm) return;

      this._findationAtcPending = false;
      document.removeEventListener('liquid-ajax-cart:request-end', this._findationAtcEndHandler);
      this._findationAtcEndHandler = null;
      this._setFindationAtcLoading(atcBtn, false);
      if (rs.responseData && rs.responseData.ok) {
        this._closeFindation();
      }
    };

    this._trackFindationListener(
      document,
      'liquid-ajax-cart:request-end',
      this._findationAtcEndHandler
    );

    if (typeof form.requestSubmit === 'function') {
      form.requestSubmit(submitBtn);
    } else {
      submitBtn.click();
    }
  }

  _setFindationAtcLoading(btn, loading) {
    if (!btn) return;
    const textEl = btn.querySelector('.phn-findation__atc-text');
    const priceEl = btn.querySelector('[data-phn-findation-atc-price]');
    if (loading) {
      if (textEl && textEl.dataset.originalAtcLabel == null) {
        textEl.dataset.originalAtcLabel = textEl.textContent.trim();
      }
      if (textEl) textEl.textContent = 'ADDING...';
      if (priceEl) priceEl.hidden = true;
      btn.disabled = true;
      btn.setAttribute('aria-busy', 'true');
      btn.classList.add('phn-findation__atc--loading');
    } else {
      if (textEl) {
        textEl.textContent = textEl.dataset.originalAtcLabel || 'Add to cart';
        delete textEl.dataset.originalAtcLabel;
      }
      if (priceEl) priceEl.hidden = false;
      btn.disabled = false;
      btn.removeAttribute('aria-busy');
      btn.classList.remove('phn-findation__atc--loading');
    }
  }

  _waitlistModalId(variantId) {
    return `phn-waitlist-modal-${variantId || 'selected'}`;
  }

  _escapeWaitlistHtml(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  _waitlistSettings(button) {
    const form = button?.closest('[data-phn-form-std]');
    return {
      productId: button?.dataset?.productId || form?.dataset?.productId || '',
      variantId: button?.dataset?.id || button?.getAttribute('data-variant-id') || '',
      copy:
        form?.dataset?.phnWaitlistDescription ||
        "Enter your email and we'll let you know when this item is back.",
      caption:
        form?.dataset?.phnWaitlistCaption ||
        'By signing up, you agree to receive a back-in-stock email for this item and accept our <a href="/policies/privacy-policy">privacy policy</a>.',
      success: form?.dataset?.phnWaitlistSuccess || "You're on the list. We'll email you as soon as it's back.",
      customBisEnabled: form?.dataset?.phnWaitlistCustomBisEnabled === 'true',
      customBisEndpoint: form?.dataset?.phnWaitlistCustomBisEndpoint || '',
    };
  }

  _openAmpWaitlist(button) {
    const settings = this._waitlistSettings(button);
    if (!settings.productId || !settings.variantId) return false;

    const modalId = this._waitlistModalId(settings.variantId);
    document.getElementById(modalId)?.remove();

    document.body.insertAdjacentHTML(
      'beforeend',
      `<div class="phn-waitlist-modal" id="${modalId}" role="dialog" aria-modal="true" aria-labelledby="${modalId}-title">
        <div class="phn-waitlist-modal__content">
          <div class="phn-waitlist-modal__el">
            <button type="button" class="phn-waitlist-modal__close" data-phn-waitlist-close aria-label="Close"></button>
            <h3 class="phn-waitlist-modal__title" id="${modalId}-title" tabindex="-1">Back in stock alert</h3>
            <div class="phn-waitlist-modal__copy">${this._escapeWaitlistHtml(settings.copy)}</div>
            <form class="phn-waitlist-modal__form" data-phn-waitlist-form novalidate>
              <div class="phn-waitlist-modal__error" data-phn-waitlist-error hidden></div>
              <input class="phn-waitlist-modal__input" name="email" type="email" placeholder="Email address" required>
              <input type="hidden" name="variantId" value="${this._escapeWaitlistHtml(settings.variantId)}">
              <input type="hidden" name="productId" value="${this._escapeWaitlistHtml(settings.productId)}">
              <button class="phn-waitlist-modal__submit" type="submit">Email me when it's back</button>
            </form>
            <div class="phn-waitlist-modal__caption">${settings.caption}</div>
          </div>
        </div>
        <button type="button" class="phn-waitlist-modal__bg" data-phn-waitlist-close aria-label="Close"></button>
      </div>`
    );

    const modal = document.getElementById(modalId);
    const close = () => modal?.remove();
    modal.querySelectorAll('[data-phn-waitlist-close]').forEach((el) => el.addEventListener('click', close));
    modal.querySelector('[data-phn-waitlist-form]')?.addEventListener('submit', (event) => {
      event.preventDefault();
      this._submitThemeWaitlistForm(event.currentTarget, settings);
    });
    modal.querySelector('.phn-waitlist-modal__title')?.focus();
    return true;
  }

  _submitThemeWaitlistForm(form, settings) {
    const successMessage = settings.success;
    const email = form.email?.value?.trim() || '';
    const productId = form.productId?.value || '';
    const variantId = form.variantId?.value || '';
    const button = form.querySelector('.phn-waitlist-modal__submit');
    const error = form.querySelector('[data-phn-waitlist-error]');

    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      if (error) {
        error.textContent = 'Please enter a valid email address.';
        error.hidden = false;
      }
      return;
    }

    // Dev-gated: route to the custom BIS service instead of AMP when enabled.
    if (settings.customBisEnabled && settings.customBisEndpoint) {
      this._submitCustomBisWaitlist(form, settings, { email, productId, variantId });
      return;
    }

    if (!window.BIS || typeof window.BIS.create !== 'function') {
      if (error) {
        error.textContent = 'Something went wrong. Please try again later.';
        error.hidden = false;
      }
      return;
    }

    if (button) button.disabled = true;
    if (error) error.hidden = true;

    window.BIS.create(email, variantId, productId, {}).then((response) => {
      if (button) button.disabled = false;
      const status = response?.status || '';
      const errors = response?.errors || {};
      if (status === 'OK') {
        const modal = form.closest('.phn-waitlist-modal');
        if (modal) {
          modal.querySelector('.phn-waitlist-modal__title').textContent = 'Thanks!';
          modal.querySelector('.phn-waitlist-modal__copy').textContent = successMessage;
          form.hidden = true;
          const caption = modal.querySelector('.phn-waitlist-modal__caption');
          if (caption) caption.hidden = true;
        }
        return;
      }
      if (error) {
        error.textContent = errors.email || errors.base || 'Something went wrong. Please try again later.';
        error.hidden = false;
      }
    });
  }

  _submitCustomBisWaitlist(form, settings, { email, productId, variantId }) {
    const button = form.querySelector('.phn-waitlist-modal__submit');
    const error = form.querySelector('[data-phn-waitlist-error]');

    if (button) button.disabled = true;
    if (error) error.hidden = true;

    fetch(settings.customBisEndpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email,
        productId,
        source: 'Jones Road PDP Waitlist',
        variantId,
      }),
    })
      .then((response) => response.json().then((data) => ({ data, response })))
      .then(({ data, response }) => {
        if (button) button.disabled = false;
        if (response.ok && data && data.ok) {
          const modal = form.closest('.phn-waitlist-modal');
          if (modal) {
            modal.querySelector('.phn-waitlist-modal__title').textContent = 'Thanks!';
            modal.querySelector('.phn-waitlist-modal__copy').textContent = settings.success;
            form.hidden = true;
            const caption = modal.querySelector('.phn-waitlist-modal__caption');
            if (caption) caption.hidden = true;
          }
          return;
        }
        if (error) {
          error.textContent = (data && data.error) || 'Something went wrong. Please try again later.';
          error.hidden = false;
        }
      })
      .catch(() => {
        if (button) button.disabled = false;
        if (error) {
          error.textContent = 'Something went wrong. Please try again later.';
          error.hidden = false;
        }
      });
  }

  /* ── Delegated click handlers (accordion / std tabs / std shade / pair add / findation) ── */
  _onDelegatedClick(e) {
    const waitlistAtc = e.target.closest('[data-phn-std-atc].js-waitlist');
    if (waitlistAtc && this.contains(waitlistAtc)) {
      e.preventDefault();
      e.stopPropagation();
      this._openAmpWaitlist(waitlistAtc);
      return;
    }

    const findationToggle = e.target.closest('[data-phn-findation-toggle]');
    if (findationToggle && this.contains(findationToggle)) {
      e.preventDefault();
      this._openFindation(findationToggle);
      return;
    }

    const findationClose = e.target.closest('[data-phn-findation-close]');
    if (findationClose && this._findationRoot?.contains(findationClose)) {
      e.preventDefault();
      this._closeFindation();
      return;
    }

    const findationOverlay = e.target.closest('[data-phn-findation-overlay]');
    if (findationOverlay && this._findationRoot?.contains(findationOverlay)) {
      e.preventDefault();
      this._closeFindation();
      return;
    }

    const findationTab = e.target.closest('[data-phn-findation-tab]');
    if (findationTab && this._findationRoot?.contains(findationTab)) {
      e.preventDefault();
      this._handleFindationTab(findationTab);
      return;
    }

    const findationSwatch = e.target.closest('[data-phn-findation-swatch]');
    if (findationSwatch && this._findationRoot?.contains(findationSwatch)) {
      e.preventDefault();
      this._handleFindationSwatch(findationSwatch);
      return;
    }

    const findationAtc = e.target.closest('[data-phn-findation-atc]');
    if (findationAtc && this._findationRoot?.contains(findationAtc)) {
      e.preventDefault();
      this._handleFindationAtc(findationAtc);
      return;
    }

    const accordion = e.target.closest('[data-phn-accordion-toggle]');
    if (accordion && this.contains(accordion)) {
      this._toggleAccordion(accordion);
      return;
    }

    const takeQuiz = e.target.closest('.phn-form-std__fys-split-col--quiz');
    if (takeQuiz && this.contains(takeQuiz)) {
      this._trackWtf('take-quiz');
    }
    const knowShade = e.target.closest('.phn-form-std__fys-split-col:not(.phn-form-std__fys-split-col--quiz)');
    if (knowShade && this.contains(knowShade)) {
      this._trackWtf('know-shade');
    }
    const shadeFinder = e.target.closest('.phn-form-std__fys');
    if (shadeFinder && this.contains(shadeFinder)) {
      this._trackWtf('shade-finder');
    }
    const reviewNav = e.target.closest('[data-phn-wtf-reviews-prev], [data-phn-wtf-reviews-next], [data-phn-wtf-reviews-dot]');
    if (reviewNav && this.contains(reviewNav)) {
      this._trackWtf('review-carousel', {
        Action: reviewNav.hasAttribute('data-phn-wtf-reviews-dot') ? 'dot' : 'arrow',
      });
    }

    const tab = e.target.closest('[data-phn-std-tab]');
    if (tab && this.contains(tab)) {
      this._trackWtf('depth-filter', { Filter: tab.getAttribute('data-category') || tab.textContent.trim() });
      this._handleStandardTab(tab);
      return;
    }

    const shadeBtn = e.target.closest('[data-phn-std-shade]');
    if (shadeBtn && this.contains(shadeBtn)) {
      this._trackWtf('shade-selected', {
        Shade: shadeBtn.getAttribute('data-variant-title') || '',
        VariantId: shadeBtn.getAttribute('data-variant-id') || '',
        Available: shadeBtn.getAttribute('data-variant-available') || '',
      });
      this._selectSwatch(shadeBtn);
      return;
    }

    const pairReveal = e.target.closest('[data-phn-pair-reveal]');
    if (pairReveal && this.contains(pairReveal)) {
      e.preventDefault();
      this._handlePairReveal(pairReveal);
      return;
    }

    const pairSwatch = e.target.closest('[data-phn-pair-swatch]');
    if (pairSwatch && this.contains(pairSwatch)) {
      e.preventDefault();
      this._handlePairSwatch(pairSwatch);
      return;
    }

    const pairBtn = e.target.closest('[data-phn-pair-add]');
    if (pairBtn && this.contains(pairBtn)) {
      e.preventDefault();
      this._handlePairAdd(pairBtn);
    }
  }

  /* ── Pair With: Find Your Shade ── */
  _handlePairReveal(revealBtn) {
    const card = revealBtn.closest('[data-phn-pair-card]');
    if (!card) return;
    const swatches = card.querySelector('[data-phn-pair-swatches]');
    const addBtn = card.querySelector('.phn-paired-add');
    if (swatches) swatches.hidden = false;
    revealBtn.hidden = true;
    if (addBtn) addBtn.hidden = false;
    // Auto-select the first available shade (matches Main).
    const firstSwatch =
      card.querySelector('[data-phn-pair-swatch]:not(.is-unavailable):not([disabled])') ||
      card.querySelector('[data-phn-pair-swatch]');
    if (firstSwatch) this._handlePairSwatch(firstSwatch);
  }

  _handlePairSwatch(swatch) {
    const card = swatch.closest('[data-phn-pair-card]');
    if (!card) return;

    card.querySelectorAll('[data-phn-pair-swatch].is-active').forEach((el) => {
      el.classList.remove('is-active');
    });
    swatch.classList.add('is-active');

    const id = swatch.getAttribute('data-variant-id');
    const addBtn = card.querySelector('.phn-paired-add');
    if (addBtn && id) addBtn.setAttribute('data-phn-pair-add', id);

    // Swap the card image to the selected variant's image.
    const img = card.querySelector('[data-phn-pair-img]');
    const newSrc = swatch.getAttribute('data-variant-image');
    if (img && newSrc) img.src = newSrc;

    // Show the selected shade name above the swatches.
    const shadeName = card.querySelector('[data-phn-pair-shade-name]');
    const title = swatch.getAttribute('data-variant-title');
    if (shadeName && title) {
      shadeName.textContent = title;
      shadeName.hidden = false;
    }
  }

  /* ── Accordions ── */
  _toggleAccordion(btn) {
    window.JR.toggleProductAccordion(btn);
  }

  /* ── Media thumbs sync ── */
  _initMediaSync() {
    this._initMediaThumbScroller();
    if (this._canHoverThumbs === undefined) {
      this._canHoverThumbs = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    }
    this.querySelectorAll(
      '.phn-media-thumbs .product-secondary-images img, .phn-media-thumbs .product-secondary-images video'
    ).forEach((thumb, idx) => {
      thumb.addEventListener('click', () => {
        const wrapper = this.querySelector('.js-productMedia .swiper-wrapper');
        // Index into the REAL slides only — `querySelectorAll('.js-productMediaSlide')`
        // includes the prepended/appended loop clones, so a raw `[idx]` is shifted
        // by one (the prev-clone sits at DOM index 0) and activates the wrong slide.
        // The real slide's offsetLeft already accounts for the leading clone's width.
        const slide = wrapper && this._getGalleryRealSlides(wrapper)[idx];
        if (wrapper && slide) {
          wrapper.style.scrollSnapType = 'none';
          // Programmatic navigation — keep the swipe clamp out of it.
          this._phnGalleryNavigating = Date.now();
          const to = slide.offsetLeft;
          const from = wrapper.scrollLeft;
          const t0 = performance.now();
          const step = (t) => {
            const p = Math.min((t - t0) / 300, 1);
            wrapper.scrollLeft = from + (to - from) * (1 - (1 - p) ** 2);
            if (p < 1) requestAnimationFrame(step);
            else wrapper.style.scrollSnapType = '';
          };
          requestAnimationFrame(step);
        }
        const allThumbs = thumb
          .closest('.product-secondary-images')
          ?.querySelectorAll('.secondary-aspect-child');
        allThumbs?.forEach((img) => {
          img.classList.remove('is-active');
          img.classList.remove('secondary-images-active');
        });
        thumb.classList.add('is-active');
        thumb.classList.add('secondary-images-active');
      });

      // Hover pre-warm (video slides only): when a desktop pointer rests on a
      // thumb whose matching main slide is a video, start fetching it so the
      // click plays almost instantly. Guards keep the side effects contained:
      //  • desktop hover-capable pointers only (no touch double-fire),
      //  • a short dwell debounce so a quick sweep across thumbs loads nothing,
      //  • skipped on data-saver / 2G,
      //  • only promotes — never plays — and is a one-shot per video (the
      //    data-src guard means an already-warmed/active slide is left alone).
      if (this._canHoverThumbs && !thumb.__phnHoverPreloadBound) {
        thumb.__phnHoverPreloadBound = true;
        thumb.addEventListener('mouseenter', () => {
          if (phnIsFrugalConnection()) return;
          const wrapper = this.querySelector('.js-productMedia .swiper-wrapper');
          const slide = wrapper && this._getGalleryRealSlides(wrapper)[idx];
          const video = slide && slide.querySelector('video');
          if (!video || !video.querySelector('source[data-src]')) return;
          if (this._hoverPreloadTimer) clearTimeout(this._hoverPreloadTimer);
          this._hoverPreloadTimer = setTimeout(() => {
            phnPromoteVideoSources(video, 'metadata');
          }, 120);
        });
        thumb.addEventListener('mouseleave', () => {
          if (this._hoverPreloadTimer) {
            clearTimeout(this._hoverPreloadTimer);
            this._hoverPreloadTimer = null;
          }
        });
      }
    });
  }

  _getGalleryRealSlides(wrapper) {
    return Array.from(wrapper.querySelectorAll('.js-productMediaSlide:not([data-phn-loop-clone])'));
  }

  _getGalleryRealIndex(wrapper) {
    const realCount = this._getGalleryRealSlides(wrapper).length;
    if (realCount <= 0) return 0;
    const rawIndex = Math.round(wrapper.scrollLeft / (wrapper.clientWidth || 1));
    if (wrapper.dataset.phnLoopReady === 'true') {
      if (rawIndex <= 0) return realCount - 1;
      if (rawIndex >= realCount + 1) return 0;
      return rawIndex - 1;
    }
    return Math.max(0, Math.min(realCount - 1, rawIndex));
  }

  _scrollGalleryToRealIndex(wrapper, index, behavior = 'smooth') {
    const realCount = this._getGalleryRealSlides(wrapper).length;
    if (realCount <= 0) return;
    const safeIndex = Math.max(0, Math.min(realCount - 1, index));
    const offsetIndex = wrapper.dataset.phnLoopReady === 'true' ? safeIndex + 1 : safeIndex;
    // Mark as programmatic so the swipe clamp doesn't treat the resulting
    // scrollend as a user swipe (see onNativeScrollEnd).
    this._phnGalleryNavigating = Date.now();
    wrapper.scrollTo({ left: offsetIndex * (wrapper.clientWidth || 1), behavior });
  }

  /** Snap to at most one slide from `originRealIndex` based on swipe distance. */
  _snapGalleryOneSlide(wrapper, originRealIndex, originScrollLeft) {
    const w = wrapper.clientWidth || 1;
    const dx = wrapper.scrollLeft - originScrollLeft;
    const threshold = w * 0.15;
    let direction = 0;
    if (dx > threshold) direction = 1;
    else if (dx < -threshold) direction = -1;
    const count = this._getGalleryRealSlides(wrapper).length;
    if (direction === 0 || count <= 1) {
      this._scrollGalleryToRealIndex(wrapper, originRealIndex, 'smooth');
      return;
    }
    let target = originRealIndex + direction;
    if (wrapper.dataset.phnLoopReady === 'true') {
      if (target < 0) target = count - 1;
      else if (target >= count) target = 0;
    } else {
      target = Math.max(0, Math.min(count - 1, target));
    }
    this._scrollGalleryToRealIndex(wrapper, target, 'smooth');
  }

  _ensureGalleryLoopClones(wrapper, preferredIndex = null) {
    if (!wrapper) return;
    if (this._phnLoopMutating) return;

    const beforeIndex = preferredIndex == null ? this._getGalleryRealIndex(wrapper) : preferredIndex;
    this._phnLoopMutating = true;
    wrapper.querySelectorAll('[data-phn-loop-clone]').forEach((slide) => slide.remove());

    const realSlides = this._getGalleryRealSlides(wrapper);
    // Mark the real first slide so CSS can find it regardless of clone shift.
    // Used by award/badge visibility rules that previously relied on `:first-child`.
    realSlides.forEach((slide, i) => slide.classList.toggle('phn-real-first', i === 0));
    if (realSlides.length <= 1) {
      delete wrapper.dataset.phnLoopReady;
      wrapper.scrollTo({ left: 0, behavior: 'auto' });
      window.setTimeout(() => {
        this._phnLoopMutating = false;
      }, 0);
      return;
    }

    const firstClone = realSlides[0].cloneNode(true);
    const lastClone = realSlides[realSlides.length - 1].cloneNode(true);
    // firstClone (at end) is the "next" target when wrapping past the last
    // slide; keeping `.phn-real-first` on it (inherited from cloneNode) means
    // the award stays visible during the loop transition, with no visual pop
    // when `_settleGalleryLoop` teleports to the real first slide.
    // lastClone (at start) clones the real last, which has no class — the
    // award is correctly hidden by CSS via `:not(.phn-real-first)`.
    firstClone.setAttribute('data-phn-loop-clone', 'next');
    lastClone.setAttribute('data-phn-loop-clone', 'prev');
    firstClone.setAttribute('aria-hidden', 'true');
    lastClone.setAttribute('aria-hidden', 'true');

    wrapper.insertBefore(lastClone, realSlides[0]);
    wrapper.appendChild(firstClone);
    wrapper.dataset.phnLoopReady = 'true';

    this._scrollGalleryToRealIndex(wrapper, beforeIndex, 'auto');
    window.setTimeout(() => {
      this._phnLoopMutating = false;
    }, 0);
  }

  _settleGalleryLoop(wrapper) {
    if (!wrapper || wrapper.dataset.phnLoopReady !== 'true') return;
    const realCount = this._getGalleryRealSlides(wrapper).length;
    if (realCount <= 1) return;
    const rawIndex = Math.round(wrapper.scrollLeft / (wrapper.clientWidth || 1));
    if (rawIndex === 0) {
      this._scrollGalleryToRealIndex(wrapper, realCount - 1, 'auto');
    } else if (rawIndex === realCount + 1) {
      this._scrollGalleryToRealIndex(wrapper, 0, 'auto');
    }
  }

  _initMediaThumbScroller() {
    const media = this.querySelector('.phn-media');
    const wrapper = media?.querySelector('.scrollable-product-secondary-images');
    const thumbs = wrapper?.querySelector('.product-secondary-images');
    const down = wrapper?.querySelector('.secondary-images-scroll-svg');
    const main = media?.querySelector('.phn-media-main .aspect-1-1');
    if (!wrapper || !thumbs || !down || !main) return;

    if (this._mediaScrollerCleanup) {
      this._mediaScrollerCleanup();
      this._mediaScrollerCleanup = null;
    }

    let up = wrapper.querySelector('.secondary-images-scroll-up-svg');
    if (!up) {
      up = document.createElement('div');
      up.className = 'secondary-images-scroll-up-svg';
      up.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="21" height="13" viewBox="0 0 21 13" fill="none" style="display: block;" aria-hidden="true" focusable="false"><path d="M2.17383 2.26953L10.4815 10.5772L18.7892 2.26953" stroke="#999999" stroke-width="2.07692" stroke-linecap="square"></path></svg>';
      up.style.display = 'none';
      wrapper.insertBefore(up, down);
    }

    let syncFrame = null;
    let syncRetry = null;

    const queueSyncControls = () => {
      if (syncFrame !== null) return;
      syncFrame = requestAnimationFrame(syncControls);
    };

    const syncControls = () => {
      syncFrame = null;
      if (window.innerWidth < 700) {
        down.style.display = 'none';
        up.style.display = 'none';
        return;
      }

      const mainHeight = main.offsetHeight;
      if (mainHeight <= 0) {
        down.style.display = 'none';
        up.style.display = 'none';
        clearTimeout(syncRetry);
        syncRetry = setTimeout(queueSyncControls, 100);
        return;
      }

      thumbs.style.height = `${mainHeight}px`;
      const hasOverflow = thumbs.scrollHeight - thumbs.clientHeight > 1;
      down.style.display = hasOverflow ? 'block' : 'none';
      up.style.display = hasOverflow && thumbs.scrollTop > 0 ? 'block' : 'none';
    };

    const scrollByStep = (direction) => {
      thumbs.scrollBy({
        top: thumbs.clientHeight * 0.3 * direction,
        behavior: 'smooth'
      });
    };

    const onDownClick = () => scrollByStep(1);
    const onUpClick = () => scrollByStep(-1);

    down.addEventListener('click', onDownClick);
    up.addEventListener('click', onUpClick);
    thumbs.addEventListener('scroll', queueSyncControls, { passive: true });
    window.addEventListener('resize', queueSyncControls);

    const mediaItems = Array.from(thumbs.querySelectorAll('img, video'));
    mediaItems.forEach((item) => {
      item.addEventListener('load', queueSyncControls);
      item.addEventListener('loadedmetadata', queueSyncControls);
    });

    let resizeObserver = null;
    if ('ResizeObserver' in window) {
      resizeObserver = new ResizeObserver(queueSyncControls);
      resizeObserver.observe(main);
      resizeObserver.observe(thumbs);
    }

    queueSyncControls();

    this._mediaScrollerCleanup = () => {
      clearTimeout(syncRetry);
      if (syncFrame !== null) {
        cancelAnimationFrame(syncFrame);
      }
      down.removeEventListener('click', onDownClick);
      up.removeEventListener('click', onUpClick);
      thumbs.removeEventListener('scroll', queueSyncControls);
      window.removeEventListener('resize', queueSyncControls);
      mediaItems.forEach((item) => {
        item.removeEventListener('load', queueSyncControls);
        item.removeEventListener('loadedmetadata', queueSyncControls);
      });
      resizeObserver?.disconnect();
    };
  }

  /* ── Mobile pagination dots ──
   * Live miracle balm renders Swiper-driven pagination bullets at the bottom
   * of the main gallery on mobile. We render plain buttons into the existing
   * `.product__media-pagination` and sync the active state to whichever slide
   * is currently snapped into view (scrollLeft/clientWidth based).
   *
   * A MutationObserver re-runs render() whenever the wrapper's children
   * change so swatch / size / tier-driven gallery swaps stay in sync without
   * any explicit hook into `miracle-balm-set-builder.js`.
   */
  _initMobilePagination() {
    const wrapper = this.querySelector('.js-productMedia .swiper-wrapper');
    const pag = this.querySelector('.js-productMediaPag');
    if (!wrapper || !pag) return;

    const render = () => {
      this._ensureGalleryLoopClones(wrapper);
      const slides = this._getGalleryRealSlides(wrapper);
      const count = slides.length;
      pag.innerHTML = '';
      if (count <= 1) return;

      for (let i = 0; i < count; i++) {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'phn-pag-bullet' + (i === 0 ? ' is-active' : '');
        b.setAttribute('aria-label', `Go to slide ${i + 1}`);
        b.dataset.slide = String(i);
        b.addEventListener('click', () => {
          this._scrollGalleryToRealIndex(wrapper, i, 'smooth');
        });
        pag.appendChild(b);
      }
    };

    let lastSetActiveIdx = -1;
    const setActive = () => {
      const idx = this._getGalleryRealIndex(wrapper);
      // Skip the DOM walk while the active slide hasn't changed — during a
      // drag this fires ~60Hz but idx only flips when scroll crosses a slide
      // boundary, so most calls would re-toggle classes to the same values.
      if (idx === lastSetActiveIdx) return;
      lastSetActiveIdx = idx;
      pag.querySelectorAll('.phn-pag-bullet').forEach((el, i) => {
        el.classList.toggle('is-active', i === idx);
      });
    };

    let raf = 0;
    const onScroll = () => {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(setActive);
    };
    wrapper.addEventListener('scroll', onScroll, { passive: true });
    wrapper.addEventListener('scrollend', () => {
      this._settleGalleryLoop(wrapper);
      setActive();
    });

    // Reset scroll to first slide whenever children change (variant swap).
    if (this._phnGalleryObserver) this._phnGalleryObserver.disconnect();
    this._phnGalleryObserver = new MutationObserver(() => {
      if (this._phnLoopMutating) return;
      render();
      this._scrollGalleryToRealIndex(wrapper, 0, 'auto');
      setActive();
    });
    this._phnGalleryObserver.observe(wrapper, { childList: true });

    render();
    this._renderMobilePagination = render;
  }

  /* ── Desktop drag-to-scroll on the main gallery ──
   * The main media row (`.phn-media-main .swiper-wrapper`) is a horizontal
   * scroll-snap container. On touch devices this works natively. On desktop,
   * mouse drag isn't a scroll gesture, so without this the gallery is stuck
   * showing only the first slide (thumbnails are the only way to navigate).
   *
   * Pointer Events with `pointerType === 'mouse'` so touch keeps using native
   * scrolling. Threshold + click suppression prevents accidental clicks on
   * award badges, share buttons, or thumbnails the cursor crosses while
   * dragging. setPointerCapture keeps tracking even if the cursor leaves the
   * element. CSS `scroll-snap-type: x mandatory` handles the snap on release.
   */
  _initDesktopGalleryDrag() {
    const wrapper = this.querySelector('.js-productMedia .swiper-wrapper');
    if (!wrapper) return;
    if (this._desktopDragCleanup) this._desktopDragCleanup();
    this._ensureGalleryLoopClones(wrapper);

    const DRAG_THRESHOLD_PX = 5;
    let activePointerId = null;
    let startX = 0;
    let startScrollLeft = 0;
    let startRealIndex = 0;
    let isDragging = false;
    // rAF-batched scrollLeft writes: pointermove can fire faster than the
    // display refresh on high-Hz mice; coalescing into one write per frame
    // removes micro-jitter and keeps the cursor-to-content motion synced with
    // paint.
    let pendingScrollLeft = null;
    let scrollRaf = 0;
    const flushScroll = () => {
      scrollRaf = 0;
      if (pendingScrollLeft != null) {
        wrapper.scrollLeft = pendingScrollLeft;
        pendingScrollLeft = null;
      }
    };
    const onPointerDown = (e) => {
      if (e.pointerType !== 'mouse') return; // let touch/pen use native scroll
      if (e.button !== 0) return; // primary mouse button only
      // Ignore drags initiated on interactive children
      if (e.target.closest('a, button, input, textarea, select, [role="button"]')) return;
      // Cancel a pending snap-restore from a previous drag so it can't re-enable
      // scroll-snap mid-way through this new drag (which would reintroduce jank).
      if (this._dragSnapRestoreTimer) {
        clearTimeout(this._dragSnapRestoreTimer);
        this._dragSnapRestoreTimer = 0;
      }
      activePointerId = e.pointerId;
      startX = e.clientX;
      startScrollLeft = wrapper.scrollLeft;
      startRealIndex = this._getGalleryRealIndex(wrapper);
      isDragging = false;
    };

    const onPointerMove = (e) => {
      if (e.pointerId !== activePointerId) return;
      const dx = e.clientX - startX;
      if (!isDragging) {
        if (Math.abs(dx) < DRAG_THRESHOLD_PX) return;
        isDragging = true;
        try { wrapper.setPointerCapture(activePointerId); } catch (_) {}
        wrapper.classList.add('is-grabbing');
        // Disable CSS scroll-snap for the duration of the drag. With
        // `scroll-snap-type: x mandatory` active, every per-frame `scrollLeft`
        // write below gets yanked back to the nearest snap point, producing the
        // jumpy/stuttery drag. Snap is restored after the release scroll settles
        // (see endDrag) so native wheel/touch swipes still snap. Mirrors the
        // thumbnail-click animation, which disables snap the same way.
        wrapper.style.scrollSnapType = 'none';
      }
      e.preventDefault();
      pendingScrollLeft = startScrollLeft - dx;
      if (!scrollRaf) scrollRaf = requestAnimationFrame(flushScroll);
    };

    const endDrag = (e) => {
      if (e && e.pointerId !== activePointerId) return;
      const wasDragging = isDragging;
      activePointerId = null;
      isDragging = false;
      wrapper.classList.remove('is-grabbing');
      // Make sure the last pending scrollLeft is committed before we read it.
      if (scrollRaf) {
        cancelAnimationFrame(scrollRaf);
        flushScroll();
      }
      if (wasDragging) {
        this._snapGalleryOneSlide(wrapper, startRealIndex, startScrollLeft);
        // Keep snap OFF through the smooth release animation (mandatory snap
        // would fight the in-flight scrollTo), then settle the loop and restore
        // snap so native wheel/touch scrolling snaps again.
        this._dragSnapRestoreTimer = window.setTimeout(() => {
          this._dragSnapRestoreTimer = 0;
          this._settleGalleryLoop(wrapper);
          wrapper.style.scrollSnapType = '';
        }, 360);
        // Suppress the synthetic click fired after pointerup at the end of a drag.
        const swallowClick = (ev) => {
          ev.stopPropagation();
          ev.preventDefault();
          wrapper.removeEventListener('click', swallowClick, true);
        };
        wrapper.addEventListener('click', swallowClick, true);
        setTimeout(() => wrapper.removeEventListener('click', swallowClick, true), 0);
      }
    };
    const onNativeScrollEnd = () => {
      if (this._phnLoopMutating) return;
      // Loop settle ONLY — no swipe clamping here. A scrollend-driven clamp
      // is unstable under rapid successive gestures: its corrective scrolls
      // get interrupted by the next gesture, every interruption fires another
      // scrollend, and the handler ends up clamping against its own
      // corrections (seen as autonomous back-and-forth sliding after the user
      // stops). One-slide-per-flick is already enforced natively by
      // `scroll-snap-stop: always` in the mobile CSS; deliberate long pans
      // moving 2+ slides are legitimate user intent and are left alone.
      this._settleGalleryLoop(wrapper);
    };

    // Block native image dragging so the cursor doesn't pick up a ghost image.
    const onDragStart = (e) => { if (activePointerId !== null) e.preventDefault(); };

    wrapper.addEventListener('pointerdown', onPointerDown);
    wrapper.addEventListener('pointermove', onPointerMove);
    wrapper.addEventListener('pointerup', endDrag);
    wrapper.addEventListener('pointercancel', endDrag);
    wrapper.addEventListener('scrollend', onNativeScrollEnd);
    // iOS Safari has no `scrollend`, so the loop settle + one-slide clamp
    // above never ran there: landing on a loop clone stayed un-teleported and
    // the next swipe read as a multi-slide jump. Emulate with a debounced
    // scroll listener on browsers without native support.
    let scrollEndEmulationTimer = 0;
    const onScrollEmulated = () => {
      clearTimeout(scrollEndEmulationTimer);
      scrollEndEmulationTimer = window.setTimeout(onNativeScrollEnd, 140);
    };
    const needsScrollEndEmulation = !('onscrollend' in window);
    if (needsScrollEndEmulation) {
      wrapper.addEventListener('scroll', onScrollEmulated, { passive: true });
    }
    wrapper.addEventListener('dragstart', onDragStart);

    this._desktopDragCleanup = () => {
      wrapper.removeEventListener('pointerdown', onPointerDown);
      wrapper.removeEventListener('pointermove', onPointerMove);
      wrapper.removeEventListener('pointerup', endDrag);
      wrapper.removeEventListener('pointercancel', endDrag);
      wrapper.removeEventListener('scrollend', onNativeScrollEnd);
      if (needsScrollEndEmulation) {
        wrapper.removeEventListener('scroll', onScrollEmulated);
        clearTimeout(scrollEndEmulationTimer);
      }
      wrapper.removeEventListener('dragstart', onDragStart);
      wrapper.classList.remove('is-grabbing');
      if (scrollRaf) {
        cancelAnimationFrame(scrollRaf);
        scrollRaf = 0;
        pendingScrollLeft = null;
      }
      // Don't leave scroll-snap disabled if we tore down mid-drag.
      if (this._dragSnapRestoreTimer) {
        clearTimeout(this._dragSnapRestoreTimer);
        this._dragSnapRestoreTimer = 0;
      }
      wrapper.style.scrollSnapType = '';
    };
  }

  /* ── Sync thumbnail active state to the current main-gallery slide ──
   * `_initMediaSync` handles thumb→main (click a thumb, scroll the main).
   * This handles main→thumb (drag/scroll the main, highlight the matching
   * thumb). Index = round(scrollLeft / clientWidth). Runs rAF-debounced.
   *
   * Touches both desktop (.product-secondary-images) and mobile
   * (.product-secondary-images-mobile) so the active state stays correct on
   * viewport changes too.
   */
  _initThumbScrollSync() {
    const wrapper = this.querySelector('.js-productMedia .swiper-wrapper');
    if (!wrapper) return;
    if (this._thumbScrollSyncCleanup) this._thumbScrollSyncCleanup();

    const ACTIVE_CLASSES = ['is-active', 'secondary-images-active'];

    let lastThumbIdx = -1;
    const setActiveThumb = () => {
      const idx = this._getGalleryRealIndex(wrapper);
      // Same micro-optimization as `setActive` in the mobile pagination:
      // during a drag this fires ~60Hz but idx is stable across most frames,
      // so skip the cross-container DOM walk when nothing has changed.
      if (idx === lastThumbIdx) return;
      lastThumbIdx = idx;
      phnPlayMainGalleryVideos(wrapper, idx);
      this.querySelectorAll(
        '.product-secondary-images .secondary-aspect-child, ' +
        '.product-secondary-images-mobile .secondary-aspect-child'
      ).forEach((thumb) => {
        ACTIVE_CLASSES.forEach((c) => thumb.classList.remove(c));
      });
      // Per-container indexing so desktop and mobile each highlight their own
      // i-th thumb (some templates render different counts in each container).
      ['.product-secondary-images', '.product-secondary-images-mobile'].forEach((sel) => {
        this.querySelectorAll(sel).forEach((container) => {
          const thumbs = container.querySelectorAll('.secondary-aspect-child');
          thumbs.forEach((t, i) => {
            if (t.tagName === 'VIDEO') phnPlayThumbVideo(t, i === idx);
          });
          const t = thumbs[idx];
          if (t) ACTIVE_CLASSES.forEach((c) => t.classList.add(c));
        });
      });
    };

    let raf = 0;
    const onScroll = () => {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(setActiveThumb);
    };

    wrapper.addEventListener('scroll', onScroll, { passive: true });
    // Initial sync (covers page load + variant swaps that reset scrollLeft).
    setActiveThumb();

    this._thumbScrollSyncCleanup = () => {
      wrapper.removeEventListener('scroll', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }

  /* ── Standard-view: apply a category filter to the swatch list ──
   * `options.autoSelectOnHidden` (default true): when the previously active
   * swatch is hidden by the new filter, auto-select the first visible one.
   * Tab clicks pass `false` so the shopper's clicked selection stays the
   * source of truth across tabs (description / gallery / cart unchanged);
   * tabs are purely a visual filter on the swatch grid in that case.
   */
  _applyCategoryFilter(wrap, categoryHandle, options = {}) {
    const autoSelectOnHidden = options.autoSelectOnHidden !== false;

    wrap.querySelectorAll('[data-phn-std-shade]').forEach((s) => {
      if (categoryHandle === 'all') {
        s.hidden = false;
        return;
      }
      const cats = (s.getAttribute('data-categories') || '').split(/\s+/).filter(Boolean);
      s.hidden = cats.indexOf(categoryHandle) === -1;
    });

    const activeSwatch = wrap.querySelector('[data-phn-std-shade].is-active');
    const needsFallback = !activeSwatch || (autoSelectOnHidden && activeSwatch.hidden);
    if (needsFallback) {
      const target =
        wrap.querySelector('[data-phn-std-shade]:not([hidden]):not([disabled])') ||
        wrap.querySelector('[data-phn-std-shade]:not([hidden])');
      if (target) this._selectSwatch(target);
    }
  }

  /** Keep the ATC in sync with the selected shade, including AMP waitlist hooks for sold-out variants. */
  _updateStandardAtcState(wrap, shadeBtn) {
    const atc = wrap.querySelector('[data-phn-std-atc]');
    const textEl = wrap.querySelector('.phn-form-std__atc-text');
    if (!atc || !textEl) return;

    const available = shadeBtn?.getAttribute('data-variant-available') !== 'false';
    const variantId = shadeBtn?.getAttribute('data-variant-id') || '';
    const hasWaitlist = wrap.dataset.phnHasWaitlist === 'true';
    const waitlistCopy = wrap.dataset.phnWaitlistCopy || 'Notify Me';
    const soldOutCopy = wrap.dataset.phnSoldOutCopy || 'Sold out';
    const addLabel = 'Add to cart';

    let label = addLabel;
    if (!available) {
      label = hasWaitlist ? waitlistCopy : soldOutCopy;
    }

    textEl.textContent = label;
    textEl.dataset.phnAtcLabel = label;
    if (variantId) atc.dataset.id = variantId;
    if (wrap.dataset.productId) atc.dataset.productId = wrap.dataset.productId;

    if (!available) {
      atc.classList.add('phn-form-std__atc--unavailable');
      if (hasWaitlist) {
        atc.disabled = false;
        atc.type = 'button';
        atc.classList.add('js-waitlist');
      } else {
        atc.disabled = true;
        atc.type = 'button';
        atc.classList.remove('js-waitlist');
      }
    } else {
      atc.disabled = false;
      atc.type = 'submit';
      atc.classList.remove('phn-form-std__atc--unavailable');
      atc.classList.remove('js-waitlist');
    }

    this._syncStickyAtcFromPrimary();
  }

  _updateKitIncludes(variantId) {
    const target = this.querySelector('[data-phn-kit-includes]');
    if (!target) return;
    const tpl = this.querySelector(`[data-phn-kit-contents="${variantId}"]`);
    target.innerHTML = tpl ? tpl.innerHTML : '';
  }

  _updateStandardShadeDescription(wrap, btn) {
    if (!wrap || !btn) return;
    if (wrap.querySelector('.phn-form-std__multi')) return;
    const title = btn.getAttribute('data-variant-title') || '';
    const subtitle = btn.getAttribute('data-variant-subtitle') || '';
    const isNew = btn.getAttribute('data-variant-is-new') === 'true';

    const nameEl = wrap.querySelector('[data-phn-std-shade-name]');
    if (nameEl) nameEl.textContent = title;

    const nameInline = wrap.querySelector('[data-phn-std-shade-name-inline]');
    if (nameInline) nameInline.textContent = `${title}\u00A0`;

    const subtitleEl = wrap.querySelector('[data-phn-std-shade-subtitle]');
    const isEl = wrap.querySelector('.phn-form-std__shade-is');
    const clean = String(subtitle || '')
      .trim()
      .replace(/^\s*is\s+/i, '');
    if (subtitleEl) {
      subtitleEl.textContent = clean;
      subtitleEl.hidden = !clean;
    }
    if (isEl) isEl.hidden = !clean;

    const newEl = wrap.querySelector('[data-phn-std-shade-new]');
    if (newEl) newEl.hidden = !isNew;
  }

  /* Apply a swatch selection: mark active, update hidden inputs, variant UI
   * copy, and rebuild the PDP gallery. Shared between click, tab-switch
   * auto-select, and initial-load sync. */
  _selectSwatch(btn) {
    if (!btn) return;
    const wrap = btn.closest('[data-phn-form-std]');
    if (!wrap) return;

    wrap.querySelectorAll('[data-phn-std-shade]').forEach((b) => b.classList.remove('is-active'));
    btn.classList.add('is-active');

    const vid = btn.getAttribute('data-variant-id');
    const hidden = wrap.querySelector('[data-phn-std-variant-id]');
    if (hidden) hidden.value = vid;
    const installment = wrap.querySelector('[data-phn-std-installment-variant]');
    if (installment) installment.value = vid;

    this._updateStandardShadeDescription(wrap, btn);
    this._updateKitIncludes(vid);
    this._updatePhnAtcPrices(wrap, btn);
    this._syncFreeShippingMessage(btn.getAttribute('data-variant-price'));
    this._updateStandardAtcState(wrap, btn);

    // Only rebuild the main gallery when Standard is the visible view. Mini
    // mode has its own tier-driven gallery swap and we don't want to fight
    // with it.
    if (vid && !document.body.classList.contains('state--mini-view')) {
      this._swapGalleryToVariant(vid);
    }

    if (
      window.history &&
      typeof window.history.replaceState === 'function' &&
      vid &&
      !document.body.classList.contains('state--mini-view')
    ) {
      try {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', vid);
        window.history.replaceState({}, '', url.toString());
      } catch {
        /* noop */
      }
    }
  }

  _handleStandardTab(btn) {
    const wrap = btn.closest('[data-phn-form-std]');
    if (!wrap) return;

    wrap.querySelectorAll('[data-phn-std-tab]').forEach((t) => {
      t.classList.remove('is-active');
      t.setAttribute('aria-selected', 'false');
    });
    btn.classList.add('is-active');
    btn.setAttribute('aria-selected', 'true');

    // Tab clicks are a pure visual filter — keep the shopper's clicked swatch
    // sticky even if it isn't in the new tab. Description/gallery/cart only
    // change on swatch hover (preview) or swatch click.
    this._applyCategoryFilter(wrap, btn.getAttribute('data-category') || 'all', {
      autoSelectOnHidden: false,
    });
  }

  _initStandardTabsInitial() {
    // On load, apply the default tab's filter and select the first visible swatch
    // in ALL so the picker, description, gallery, and cart match variant order.
    // When category tabs are omitted (quiz PDP), treat as “ALL” so every swatch stays visible.
    const urlVariant = new URLSearchParams(window.location.search).get('variant');

    this.querySelectorAll('[data-phn-form-std]').forEach((wrap) => {
      const firstTab = wrap.querySelector('[data-phn-std-tab].is-active');
      const category = firstTab?.getAttribute('data-category') || 'all';

      this._applyCategoryFilter(wrap, category);

      if (!urlVariant && category === 'all') {
        const firstSwatch =
          wrap.querySelector('[data-phn-std-shade]:not([hidden]):not([disabled])') ||
          wrap.querySelector('[data-phn-std-shade]:not([hidden])');
        if (firstSwatch) this._selectSwatch(firstSwatch);
      }
    });
  }

  /**
   * After tabs + swatch filter run, re-apply the active swatch (including
   * `_swapGalleryToVariant` from embedded JSON) so the left gallery matches
   * hover/click without requiring interaction.
   */
  _syncActiveSwatchGalleriesOnLoad() {
    if (document.body.classList.contains('state--mini-view')) {
      this._clearGalleryPending();
      return;
    }
    this.querySelectorAll('[data-phn-form-std]').forEach((wrap) => {
      let active = wrap.querySelector('[data-phn-std-shade].is-active:not([hidden])');
      if (!active) {
        active = wrap.querySelector('[data-phn-std-shade]:not([hidden]):not([disabled])');
      }
      if (active) {
        this._selectSwatch(active);
      } else {
        // Single-variant product (no shade picker, e.g. The Bucket Hat): there's
        // no swatch to drive the swap, so apply the selected variant's media
        // directly. This brings in the variant carousel + appended product
        // images so the gallery isn't limited to server-rendered product.media.
        const vidEl = wrap.querySelector('[data-phn-std-variant-id]');
        const vid = vidEl && vidEl.value;
        if (vid) this._swapGalleryToVariant(vid);
      }
    });
    /* Safety: reveal gallery if no swatch swap ran (edge cases). */
    this._clearGalleryPending();
  }

  /** Removes SSR “pending” cloak once JSON gallery is applied or we fall back to SSR. */
  _clearGalleryPending() {
    this.querySelector('.phn-media.phn-media--gallery-pending')?.classList.remove('phn-media--gallery-pending');
  }

  /* ── Variant media cache (lazy parse of the embedded JSON) ──
   * For the IG treatment group this returns the custom.ig_pdp_gallery media
   * (main gallery + thumbnail rails both use it); everyone else gets the
   * control map. Variants without IG media fall back to control.
   */
  _getVariantMedia(variantId) {
    if (this._variantMediaCache == null) {
      const el = this.querySelector('[data-phn-std-variant-media]');
      if (!el) {
        this._variantMediaCache = {};
      } else {
        try {
          this._variantMediaCache = JSON.parse(el.textContent) || {};
        } catch {
          this._variantMediaCache = {};
        }
      }
    }
    const id = String(variantId);
    if (this._igActive) {
      if (this._igMediaCache == null) {
        const igEl = this.querySelector('[data-phn-std-variant-media-ig]');
        try {
          this._igMediaCache = igEl ? JSON.parse(igEl.textContent) || {} : {};
        } catch {
          this._igMediaCache = {};
        }
      }
      const igMedia = this._igMediaCache[id] || this._igMediaCache[variantId];
      if (igMedia && igMedia.length) return igMedia;
    }
    return this._variantMediaCache[id] || this._variantMediaCache[variantId] || [];
  }

  /* ── IG media gallery test ──
   * The first-paint swap to custom.ig_pdp_gallery media runs in the section's
   * inline script (Intelligems is render-blocking, so the group is known at
   * parse time). Here we only flag the treatment group so later swatch changes
   * keep using the IG media via `_getVariantMedia`. Variants without IG media
   * fall back to the control map, so partial rollout stays safe.
   */
  _initIgGallery() {
    const igData = this.querySelector('[data-phn-std-variant-media-ig]');
    if (!igData) return;
    const testId = igData.dataset.igTestId;
    const treatmentId = igData.dataset.igTreatmentId;
    if (!testId || !treatmentId) return;
    const setActive = () => {
      const group = window.igData?.user?.getTestGroup?.(testId);
      if (group && group.id === treatmentId) this._igActive = true;
    };
    setActive();
    if (!this._igActive) {
      window.addEventListener('ig:ready', setActive, { once: true });
    }
  }

  /**
   * Swap the main PDP gallery + thumbnail rails to the given variant's
   * carousel. Mirrors the changeMedia shim in `miracle-balm-set-builder.js` so
   * both paths produce identical DOM.
   */
  _swapGalleryToVariant(variantId) {
    try {
      if (!variantId) return;

      const mediaRoot = this.querySelector('.js-productMedia');
      const wrapper = mediaRoot && mediaRoot.querySelector('.swiper-wrapper');

      const media = this._getVariantMedia(variantId);
      if (!media.length) return;

      // SSR now paints the active variant's carousel (phn-hero-variant-media).
      // Skip a no-op rebuild when the wrapper already matches this variant so
      // connectedCallback's initial sync doesn't tear down first-paint images.
      // Genuine shade changes pass a different variantId, so the swap still runs.
      if (
        wrapper &&
        wrapper.dataset.phnActiveVariant === String(variantId) &&
        this._getGalleryRealSlides(wrapper).length > 0
      ) {
        this._clearGalleryPending();
        phnPlayMainGalleryVideos(wrapper);
        return;
      }

      const slidesHtml = media.map(phnBuildSlideHtml).join('');
      // Thumbnails mirror the main gallery (IG treatment swaps both rails too).
      const thumbsHtml = media.map(phnBuildThumbHtml).join('');

      if (mediaRoot && wrapper) {
        wrapper.innerHTML = slidesHtml;
        wrapper.setAttribute('data-phn-active-variant', String(variantId));
        // innerHTML wiped the previous variant's loop clones, but the stale
        // `data-phn-loop-ready` flag + scrollLeft:0 makes `_getGalleryRealIndex`
        // resolve to the LAST real slide (scroll pos 0 == prev-clone when looped).
        // Clear the loop state and reset scroll so the rebuilt gallery starts on
        // the first slide instead of jumping to the end (seen on the quiz results page).
        delete wrapper.dataset.phnLoopReady;
        // Programmatic reset — keep the swipe clamp from reacting to it.
        this._phnGalleryNavigating = Date.now();
        wrapper.scrollLeft = 0;
      }
      this.querySelectorAll('.phn-media-thumbs .product-secondary-images:not(.js-mbModalThumbs)').forEach((el) => {
        el.innerHTML = thumbsHtml;
      });
      const secMobile = this.querySelector('.product-secondary-images-mobile');
      if (secMobile) secMobile.innerHTML = thumbsHtml;

      // Re-bind the thumb -> main-slide click sync now that the thumbs just re-rendered.
      this._initMediaSync();
      // Re-render mobile pagination dots for the new slide count.
      if (typeof this._renderMobilePagination === 'function') {
        this._renderMobilePagination();
      }
      phnCloneHeroAwardsToSlides(this);
      phnPlayMainGalleryVideos(wrapper);
    } finally {
      this._clearGalleryPending();
    }
  }

  /* ── Standard-view hover preview ──
   * Debounce rapid mouseenters while the shopper sweeps across swatches so we
   * don't thrash the DOM or re-trigger lazy-loads on every pixel.
   * `mouseenter` doesn't bubble, so we attach handlers directly. The
   * `__phnHoverBound` flag prevents double-binding within a single connect.
   */
  _initStandardHoverPreview() {
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

    this.querySelectorAll('[data-phn-form-std]').forEach((wrap) => {
      const list = wrap.querySelector('.phn-form-std__swatches-list');
      if (!list || list.__phnHoverLeaveBound) return;
      list.__phnHoverLeaveBound = true;
      list.addEventListener('mouseleave', () => {
        if (this._hoverTimer) clearTimeout(this._hoverTimer);
        const active = wrap.querySelector('[data-phn-std-shade].is-active');
        if (!active) return;
        this._updateStandardShadeDescription(wrap, active);
        this._updateKitIncludes(active.getAttribute('data-variant-id'));
        // Restore the gallery to the selected swatch — without this the hero
        // keeps showing the last-hovered shade's media after the pointer leaves.
        const activeVid = active.getAttribute('data-variant-id');
        if (activeVid) {
          this._hoverTimer = setTimeout(() => this._swapGalleryToVariant(activeVid), 80);
        }
      });
    });

    this.querySelectorAll('[data-phn-std-shade]').forEach((btn) => {
      if (btn.__phnHoverBound) return;
      btn.__phnHoverBound = true;
      btn.addEventListener('mouseenter', () => {
        if (btn.hasAttribute('disabled')) return;
        const vid = btn.getAttribute('data-variant-id');
        if (!vid) return;
        const wrap = btn.closest('[data-phn-form-std]');
        if (wrap) this._updateStandardShadeDescription(wrap, btn);
        this._updateKitIncludes(vid);
        if (this._hoverTimer) clearTimeout(this._hoverTimer);
        this._hoverTimer = setTimeout(() => this._swapGalleryToVariant(vid), 80);
      });
    });
  }

  /* ── Size toggle resync ──
   * When the shopper toggles the size selector back to Standard, Mini's own
   * URL-param driven gallery swap leaves the hero showing mini media. Re-sync
   * to the currently-active standard swatch so the carousel matches.
   */
  _initSizeToggleResync() {
    document.querySelectorAll('.js-mbSizeToggle[data-size="standard"]').forEach((btn) => {
      if (btn.__phnStdSyncBound) return;
      btn.__phnStdSyncBound = true;
      btn.addEventListener('click', () => {
        // miracle-balm-set-builder flips body.state--mini-view; wait a tick.
        setTimeout(() => {
          this.querySelectorAll('[data-phn-form-std]').forEach((wrap) => {
            const activeTab = wrap.querySelector('[data-phn-std-tab].is-active');
            const cat = activeTab ? activeTab.getAttribute('data-category') || 'all' : 'all';
            this._applyCategoryFilter(wrap, cat);
            // If the previously-active swatch is still valid for this tab,
            // applyCategoryFilter won't have re-selected — force a gallery
            // resync now that we're back in standard mode.
            const active = wrap.querySelector('[data-phn-std-shade].is-active');
            if (active) this._selectSwatch(active);
          });
        }, 30);
      });
    });
  }

  /* ── Pair With quick-add ── */
  _handlePairAdd(btn) {
    // Internal in-flight guard via data-attr (not `btn.disabled`) so the user
    // doesn't see a greyed/disabled visual while the request is pending.
    if (btn.dataset.phnAdding === 'true') return;
    const id = parseInt(btn.getAttribute('data-phn-pair-add'), 10);
    if (!id) return;
    const label = btn.querySelector('.phn-paired-btn__text');
    const price = btn.querySelector('.phn-paired-btn__price');
    const orig = label ? label.textContent : '';
    btn.dataset.phnAdding = 'true';
    // Match the primary ATC: show "ADDING..." while the request is in flight,
    // then settle back to the original label once the cart drawer is visible.
    // Use inline `display: none` because the `.phn-paired-btn__price` rule sets
    // `display: inline-flex`, which overrides the UA `[hidden] { display: none }`.
    if (label) label.textContent = 'ADDING...';
    if (price) price.style.display = 'none';

    const restore = () => {
      if (label) label.textContent = orig || 'Add To Bag';
      if (price) price.style.display = '';
      delete btn.dataset.phnAdding;
    };
    // Notify non-LAC listeners (e.g. miracle-balm cartModel) — they re-read
    // /cart.js, not write — then settle the button label back. The drawer
    // opens on this same request-end: component-cart-drawer.js dispatches
    // `cart-open` and Alpine flips the `cart-open` class on the drawer wrapper
    // as it slides in. Restore only once that class is actually present — a
    // fixed delay could reset the label while the drawer is still opening if
    // the transition or main thread runs long. Capped so it can never stick.
    const restoreWhenCartVisible = () => {
      if (document.querySelector('.cart-open')) {
        restore();
        return;
      }
      let poll = null;
      let cap = null;
      const settle = () => {
        clearInterval(poll);
        clearTimeout(cap);
        restore();
      };
      poll = setInterval(() => {
        if (document.querySelector('.cart-open')) settle();
      }, 100);
      cap = setTimeout(settle, 2000);
    };
    const afterAdd = () => {
      if (typeof window.refreshCart === 'function') {
        window.refreshCart();
      } else {
        document.dispatchEvent(new CustomEvent('cart:refresh'));
      }
      restoreWhenCartVisible();
    };

    // Single cart request: liquidAjaxCart.add bundles the add + section
    // re-render and fires request-end (drawer open + header sync via
    // component-cart-drawer.js). The legacy path did a raw add.js followed by
    // liquidAjaxCart.update({}) inside phnPairWithRefreshAndOpenCart — two
    // serialized cart writes (the add.js + update.js pair).
    if (window.liquidAjaxCart?.add) {
      window.liquidAjaxCart.add(
        { items: [{ id, quantity: 1 }] },
        {
          info: { initiator: 'phn-pair-with' },
          lastCallback: (requestState) => {
            if (requestState?.responseData?.ok) afterAdd();
            else restore();
          },
        }
      );
      return;
    }

    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

    fetch(`${root}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id, quantity: 1 }] }),
    })
      .then((r) => r.json())
      .then(() => {
        phnPairWithRefreshAndOpenCart();
        afterAdd();
      })
      .catch(restore);
  }

  /**
   * Update primary ATC sale/regular prices after a shade change.
   * Mirrors live `productForm.js → updateButtonCopy` (regular) plus per-variant
   * compare-at in the sticky clone path.
   */
  _updatePhnAtcPrices(wrap, shadeBtn) {
    if (!wrap) return;
    const pricesWrap = wrap.querySelector('[data-phn-std-atc-prices]');
    if (!pricesWrap) return;

    const priceFormatted = shadeBtn?.getAttribute('data-variant-price-formatted') || '';
    const compareFormatted = shadeBtn?.getAttribute('data-variant-compare-at-price-formatted') || '';
    const isDiscount = pricesWrap.classList.contains('phn-form-std__atc-prices--discount_metafield');
    const saleEl = pricesWrap.querySelector('[data-phn-atc-price-sale]');
    const regularEl = pricesWrap.querySelector('[data-phn-atc-price-regular]');

    if (isDiscount) {
      if (saleEl && priceFormatted) saleEl.textContent = priceFormatted;
      if (regularEl?.hasAttribute('data-metafield-price')) {
        const currency = regularEl.getAttribute('data-currency');
        const metaPrice = regularEl.getAttribute('data-metafield-price');
        regularEl.textContent =
          currency && currency !== 'USD' ? `${metaPrice} ${currency}` : metaPrice;
      }
    } else {
      if (regularEl && priceFormatted) regularEl.textContent = priceFormatted;
      if (saleEl) {
        if (compareFormatted) {
          saleEl.textContent = compareFormatted;
          saleEl.hidden = false;
        } else {
          saleEl.textContent = '';
          saleEl.hidden = true;
        }
      }
    }

    this._syncStickyAtcPricesFromPrimary();
  }

  /** Keep the PDP free-shipping line in sync with the selected variant price.
   * US-only: `component-pdp-free-shipping` renders the threshold and both
   * copies as data attributes; other markets have no `data-fs-threshold` and
   * are left untouched. */
  _syncFreeShippingMessage(priceCents) {
    const price = Number(priceCents);
    if (!Number.isFinite(price)) return;
    const root = this._getPhnSectionRoot() || this;
    root.querySelectorAll('[data-pdp-free-shipping][data-fs-threshold]').forEach((el) => {
      const threshold = Number(el.getAttribute('data-fs-threshold'));
      const qualifies = Number.isFinite(threshold) && price >= threshold;
      const copy = qualifies
        ? el.getAttribute('data-fs-qualify-copy') || ''
        : el.getAttribute('data-fs-fallback-copy') || '';
      const text = el.querySelector('[data-fs-text]');
      if (text) text.innerHTML = copy;
      el.hidden = !copy;
    });
  }

  /** Mirror primary ATC price spans onto the sticky clone. */
  _syncStickyAtcPricesFromPrimary() {
    const primaryPrices = this.querySelector('[data-phn-std-atc-prices]');
    const stickyPrices = this._getPhnSectionRoot()?.querySelector('[data-phn-sticky-atc-prices]');
    if (!primaryPrices || !stickyPrices) return;

    const primarySale = primaryPrices.querySelector('[data-phn-atc-price-sale]');
    const stickySale = stickyPrices.querySelector('[data-phn-atc-price-sale]');
    const primaryRegular = primaryPrices.querySelector('[data-phn-atc-price-regular]');
    const stickyRegular = stickyPrices.querySelector('[data-phn-atc-price-regular]');

    if (primarySale && stickySale) {
      stickySale.textContent = primarySale.textContent;
      if (primarySale.hidden) {
        stickySale.hidden = true;
      } else {
        stickySale.removeAttribute('hidden');
      }
    }
    if (primaryRegular && stickyRegular) {
      stickyRegular.textContent = primaryRegular.textContent;
    }
  }

  /* ── Standard ATC loading state (Liquid Ajax Cart) ── */
  _setStandardAtcLoading(btn, loading) {
    if (!btn) return;
    const textEl = btn.querySelector('.phn-form-std__atc-text');
    const pricesEl = btn.querySelector('[data-phn-std-atc-prices]');
    if (loading) {
      if (textEl && textEl.dataset.originalAtcLabel == null) {
        textEl.dataset.originalAtcLabel = textEl.textContent.trim();
      }
      if (textEl) textEl.textContent = 'ADDING...';
      if (pricesEl) pricesEl.hidden = true;
      btn.disabled = true;
      btn.setAttribute('aria-busy', 'true');
      btn.classList.add('phn-form-std__atc--loading');
    } else {
      if (textEl) {
        const wrap = btn.closest('[data-phn-form-std]');
        if (wrap?.querySelector('.phn-form-std__multi')) {
          delete textEl.dataset.originalAtcLabel;
          const active = wrap.querySelector('[data-phn-std-shade].is-active');
          if (active) this._updateStandardAtcState(wrap, active);
        } else {
          textEl.textContent = textEl.dataset.originalAtcLabel || 'Add to cart';
          delete textEl.dataset.originalAtcLabel;
          btn.disabled = false;
        }
      }
      if (pricesEl) pricesEl.hidden = false;
      btn.removeAttribute('aria-busy');
      btn.classList.remove('phn-form-std__atc--loading');
    }
  }

  /** Locate this hero's standard ATC button if the cart event came from inside it. */
  _ownStandardAtcFromInitiator(initiator) {
    if (!initiator || typeof initiator.querySelector !== 'function') return null;
    if (!this.contains(initiator)) return null;
    return initiator.querySelector('[data-phn-std-atc]');
  }

  _onAjaxStart(e) {
    const rs = e.detail && e.detail.requestState;
    if (!rs || rs.requestType !== 'add') return;
    const btn = this._ownStandardAtcFromInitiator(rs.info && rs.info.initiator);
    if (btn) {
      this._setStandardAtcLoading(btn, true);
      this._syncStickyAtcFromPrimary();
    }
  }

  _onAjaxEnd(e) {
    const rs = e.detail && e.detail.requestState;
    if (!rs || rs.requestType !== 'add') return;
    const btn = this._ownStandardAtcFromInitiator(rs.info && rs.info.initiator);
    if (btn) {
      this._setStandardAtcLoading(btn, false);
      this._syncStickyAtcFromPrimary();
    }
    // Drawer open is handled by component-cart-drawer.js on the same request-end.
    // A second cart-open here re-ran samples/GWP init and added perceived latency.
  }

  /* ── Sticky ATC ────────────────────────────────────────────────────────────
   * Adapted for this theme's header (`sections/header.liquid`), which is
   * `position: sticky; top: 0` and only un-hides itself when `scrollY === 0`.
   * The live theme's `scrolling-up` body class is therefore irrelevant here —
   * once the sticky bar appears, it stays visible regardless of scroll
   * direction. It only hides again when the user scrolls back UP past the
   * sentinel (which puts the sentinel back into the viewport).
   *
   * The sticky markup lives in `snippets/phn-product-sticky-form.liquid` and
   * reuses the live's class hooks (`.product__sticky`, `.product__sticky--mini`)
   * so the existing `miracle-balm-set-builder.js` mini-sticky path keeps
   * working without modification.
   *
   * Responsibilities here (standard / non-MB sticky only — Miracle Balm mini
   * sticky owns its own clone via `syncStickyMiniAtc`):
   *   1. IntersectionObserver on `.js-stickyAtcObserve` toggles
   *      `body.state--sticky-atc` (slide-in via CSS transitions).
   *   2. Sticky ATC click proxies to the primary `[data-phn-std-atc]` submit.
   *   3. MutationObserver on the primary ATC text/price keeps the sticky clone
   *      in sync (variant changes update both buttons simultaneously).
   * ──────────────────────────────────────────────────────────────────────────*/
  _initStickyAtc() {
    const sentinel = this.querySelector('.js-stickyAtcObserve');
    if (!sentinel) return;

    const sectionRoot = this._getPhnSectionRoot();
    const multiPhn = this._isMultiPhnPage();

    // IntersectionObserver — mirrors live `app/components/productForm/stickyAddToCart.js`:
    // sentinel has `top: 100vh` (see `phn-product-sticky-form.css`). Show sticky
    // when the sentinel intersects OR has scrolled past the viewport top.
    this._stickyAtcObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (sentinel.closest('.quiz-pending')) {
            if (multiPhn) this._syncPhnStickyVisibility();
            else document.body.classList.remove('state--sticky-atc');
            return;
          }
          if (multiPhn) {
            this._syncPhnStickyVisibility();
            return;
          }
          const { isIntersecting, boundingClientRect, intersectionRect } = entry;
          const hasPassed = boundingClientRect.bottom <= intersectionRect.bottom;
          if (isIntersecting || hasPassed) {
            document.body.classList.add('state--sticky-atc');
          } else {
            document.body.classList.remove('state--sticky-atc');
            this._closeStickyShadeIfOpen();
          }
        });
      },
      { threshold: 1.0, rootMargin: '0px' }
    );
    this._stickyAtcObserver.observe(sentinel);

    // Footer clearance: publish the rendered bar height as `--phn-sticky-height`
    // on <html>; `phn-product-sticky-form.css` offsets the footer by it while
    // `state--sticky-atc` is up (mobile bottom bar only). ResizeObserver catches
    // content-driven height changes (MB standard ⇄ mini swap — the display:none
    // bar reports 0 — and viewport resizes, since the bars span full width).
    this._stickyBarResizeObserver = new ResizeObserver(() => ProductHeroNew._syncStickyBarHeightVar());
    sectionRoot?.querySelectorAll('[data-phn-sticky-bar]').forEach((bar) => {
      this._stickyBarResizeObserver.observe(bar);
    });
    ProductHeroNew._syncStickyBarHeightVar();

    // NOTE: this theme's header (`sections/header.liquid`) does NOT re-show on
    // scroll-up (the unsticky branch is commented out — see header.liquid 820),
    // so the live theme's `body.scrolling-up` toggle has no useful counterpart
    // here. Once the sticky bar is shown, it stays visible regardless of
    // scroll direction; the IntersectionObserver above removes `state--sticky-atc`
    // when the user scrolls back UP past the sentinel.

    // Wire the sticky ATC click → primary form's ATC click.
    const stickyAtc = sectionRoot?.querySelector('[data-phn-sticky-atc]');
    if (stickyAtc) {
      stickyAtc.addEventListener('click', this._onStickyAtcClick);
      // Initial paint sync (price/label may differ from default Liquid output if a
      // ?variant=… deep-link set a non-default selected variant after server render).
      this._syncStickyAtcFromPrimary();
    }

    // MutationObserver: when the primary ATC's text/price/disabled changes
    // (variant switch, ajax loading state), mirror to the sticky clone.
    const primaryAtc = this.querySelector('[data-phn-std-atc]');
    if (primaryAtc && stickyAtc) {
      this._stickyAtcMutationObserver = new MutationObserver(() => this._syncStickyAtcFromPrimary());
      this._stickyAtcMutationObserver.observe(primaryAtc, {
        childList: true,
        subtree: true,
        characterData: true,
        attributes: true,
        attributeFilter: ['disabled', 'class', 'aria-busy']
      });
    }

    // Sticky shade picker (only present for products with a Shade option +
    // >1 variants — see `snippets/phn-product-sticky-form.liquid`).
    this._initStickyShadePicker();
    this._initStickyJunipReviewFormat();

    // Mini sticky scroll hooks: `state--announcement-hidden` (desktop show gate)
    // and `scrolling-up` (desktop hide on scroll up). Mobile only needs
    // `state--selected-shades-full` — see `phn-product-sticky-form.css`.
    this._initAnnouncementHiddenTrigger();

    if (multiPhn) this._syncPhnStickyVisibility();
  }

  // Tallest rendered `[data-phn-sticky-bar]` → `--phn-sticky-height` on <html>.
  // Hidden bars (MB mini/standard in their off state) are display:none and
  // report offsetHeight 0, so the visible bar wins.
  static _syncStickyBarHeightVar() {
    let height = 0;
    document.querySelectorAll('[data-phn-sticky-bar]').forEach((bar) => {
      if (bar.offsetHeight > height) height = bar.offsetHeight;
    });
    document.documentElement.style.setProperty('--phn-sticky-height', height + 'px');
  }

  _onQuizResultsStickyReset() {
    document.body.classList.remove('state--sticky-atc');
    requestAnimationFrame(() => {
      const sentinel = this.querySelector('.js-stickyAtcObserve');
      if (!sentinel || !this._stickyAtcObserver || sentinel.closest('.quiz-pending')) return;
      this._stickyAtcObserver.unobserve(sentinel);
      this._stickyAtcObserver.observe(sentinel);
      if (this._isMultiPhnPage()) this._syncPhnStickyVisibility();
    });
  }

  _destroyStickyAtc() {
    if (this._stickyAtcObserver) {
      this._stickyAtcObserver.disconnect();
      this._stickyAtcObserver = null;
    }
    if (this._stickyAtcMutationObserver) {
      this._stickyAtcMutationObserver.disconnect();
      this._stickyAtcMutationObserver = null;
    }
    if (this._stickyBarResizeObserver) {
      this._stickyBarResizeObserver.disconnect();
      this._stickyBarResizeObserver = null;
    }
    const stickyAtc = this._getPhnSectionRoot()?.querySelector('[data-phn-sticky-atc]');
    if (stickyAtc) stickyAtc.removeEventListener('click', this._onStickyAtcClick);
    if (this._isMultiPhnPage()) {
      this._getPhnSectionRoot()?.querySelectorAll('.product__sticky').forEach((sticky) => {
        sticky.classList.remove('is-phn-sticky-visible');
      });
      this._syncPhnStickyVisibility();
    } else {
      document.body.classList.remove('state--sticky-atc');
    }
    this._destroyStickyShadePicker();
    this._destroyStickyJunipReviewFormat();
    this._destroyAnnouncementHiddenTrigger();
    if (!this._isMultiPhnPage()) document.body.classList.remove('phn-multi-hero');
  }

  /**
   * Mirrors live `product-media.js` 153–155: Junip renders "(84,947)" in the sticky
   * bar; live rewrites to "84946 Reviews" inline. Scoped to `.product__sticky` only.
   */
  _formatStickyJunipReviewCount() {
    const countEl = this._getPhnSectionRoot()?.querySelector(
      '.product__sticky .junip-product-summary-review-count'
    );
    if (!countEl) return;
    const html = String(countEl.innerHTML);
    if (!html.includes('(')) return;
    countEl.innerHTML = html.replace(/[,()\s]/g, '') + ' Reviews';
  }

  _initStickyJunipReviewFormat() {
    const starsRoot = this._getPhnSectionRoot()?.querySelector('.product__sticky .product__stars-container');
    if (!starsRoot) return;

    this._formatStickyJunipReviewCount();

    this._stickyJunipObserver = new MutationObserver(() => {
      this._formatStickyJunipReviewCount();
    });
    this._stickyJunipObserver.observe(starsRoot, {
      childList: true,
      subtree: true,
      characterData: true
    });
  }

  _destroyStickyJunipReviewFormat() {
    if (this._stickyJunipObserver) {
      this._stickyJunipObserver.disconnect();
      this._stickyJunipObserver = null;
    }
  }

  _onStickyAtcClick(e) {
    e.preventDefault();
    const primary = this.querySelector('[data-phn-std-atc]');
    if (primary && primary.classList.contains('js-waitlist')) {
      this._openAmpWaitlist(primary);
      return;
    }
    if (primary && !primary.disabled) primary.click();
  }

  /** Copy text / price / disabled / loading from the primary ATC button to the sticky clone. */
  _syncStickyAtcFromPrimary() {
    const primary = this.querySelector('[data-phn-std-atc]');
    const clone = this._getPhnSectionRoot()?.querySelector('[data-phn-sticky-atc]');
    if (!primary || !clone) return;

    const primaryText = primary.querySelector('.phn-form-std__atc-text');
    const cloneText = clone.querySelector('[data-phn-sticky-atc-text]');
    if (primaryText && cloneText) cloneText.textContent = primaryText.textContent;

    this._syncStickyAtcPricesFromPrimary();

    const primaryPrices = primary.querySelector('[data-phn-std-atc-prices]');
    const clonePrices = clone.querySelector('[data-phn-sticky-atc-prices]');
    if (primaryPrices && clonePrices) {
      clonePrices.hidden = primaryPrices.hidden;
    }

    clone.disabled = !!primary.disabled;
    clone.type = primary.type || 'button';
    if (primary.dataset.id) clone.dataset.id = primary.dataset.id;
    else delete clone.dataset.id;
    if (primary.dataset.productId) clone.dataset.productId = primary.dataset.productId;
    else delete clone.dataset.productId;
    clone.classList.toggle('js-waitlist', primary.classList.contains('js-waitlist'));
    clone.classList.toggle('phn-form-std__atc--loading', primary.classList.contains('phn-form-std__atc--loading'));
    clone.classList.toggle(
      'phn-form-std__atc--unavailable',
      primary.classList.contains('phn-form-std__atc--unavailable')
    );
    if (primary.getAttribute('aria-busy') === 'true') {
      clone.setAttribute('aria-busy', 'true');
    } else {
      clone.removeAttribute('aria-busy');
    }

    // Piggyback on every primary-ATC mutation to re-sync the sticky shade picker.
    // Variant switches always update the primary ATC's price/text, so this is
    // guaranteed to fire on the same tick as a shade change.
    this._syncStickyShadeFromActive();
  }

  /* ─── Sticky shade (desktop interactive + mobile static) ──────────────────
   * Desktop: toggle + overlay dropdown; item click proxies to `[data-phn-std-shade]`.
   * Mobile: no toggle in DOM — sync only updates static label/swatch hooks.
   * Document-level click + Escape close the desktop dropdown when open.
   * ──────────────────────────────────────────────────────────────────────── */
  _initStickyShadePicker() {
    const stickyRoot = this._getPhnSectionRoot()?.querySelector('[data-phn-sticky-root]');
    if (!stickyRoot) return;

    const root = stickyRoot.querySelector('[data-phn-sticky-shade]');
    const toggle = root && root.querySelector('[data-phn-sticky-shade-toggle]');

    if (root && toggle) {
      this._stickyShadeRoot = root;
      toggle.addEventListener('click', this._onStickyShadeToggle);
      const closeBtn = root.querySelector('[data-phn-sticky-shade-close]');
      if (closeBtn) closeBtn.addEventListener('click', this._onStickyShadeClose);
      root.querySelectorAll('[data-phn-sticky-shade-item]').forEach((item) => {
        item.addEventListener('click', this._onStickyShadeItemClick);
      });
      document.addEventListener('click', this._onStickyShadeDocClick);
      document.addEventListener('keydown', this._onStickyShadeKeydown);
    }

    this._syncStickyShadeFromActive();
  }

  _destroyStickyShadePicker() {
    if (!this._stickyShadeRoot) return;
    const toggle = this._stickyShadeRoot.querySelector('[data-phn-sticky-shade-toggle]');
    if (toggle) toggle.removeEventListener('click', this._onStickyShadeToggle);
    const closeBtn = this._stickyShadeRoot.querySelector('[data-phn-sticky-shade-close]');
    if (closeBtn) closeBtn.removeEventListener('click', this._onStickyShadeClose);
    this._stickyShadeRoot.querySelectorAll('[data-phn-sticky-shade-item]').forEach((item) => {
      item.removeEventListener('click', this._onStickyShadeItemClick);
    });
    document.removeEventListener('click', this._onStickyShadeDocClick);
    document.removeEventListener('keydown', this._onStickyShadeKeydown);
    this._stickyShadeRoot = null;
  }

  _onStickyShadeClose(e) {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    this._setStickyShadeOpen(false);
  }

  _onStickyShadeToggle(e) {
    e.preventDefault();
    e.stopPropagation();
    this._setStickyShadeOpen(!this._isStickyShadeOpen());
  }

  _isStickyShadeOpen() {
    return !!(this._stickyShadeRoot && this._stickyShadeRoot.classList.contains('is-open'));
  }

  _setStickyShadeOpen(open) {
    if (!this._stickyShadeRoot) return;
    const toggle = this._stickyShadeRoot.querySelector('[data-phn-sticky-shade-toggle]');
    const dd = this._stickyShadeRoot.querySelector('[data-phn-sticky-shade-dropdown]');
    if (!toggle || !dd) return;
    this._stickyShadeRoot.classList.toggle('is-open', open);
    toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    dd.setAttribute('aria-hidden', open ? 'false' : 'true');
  }

  _closeStickyShadeIfOpen() {
    if (this._isStickyShadeOpen()) {
      this._setStickyShadeOpen(false);
    }
  }

  _onStickyShadeDocClick(e) {
    if (!this._isStickyShadeOpen()) return;
    if (this._stickyShadeRoot && this._stickyShadeRoot.contains(e.target)) return;
    this._setStickyShadeOpen(false);
  }

  _onStickyShadeKeydown(e) {
    if (e.key === 'Escape' && this._isStickyShadeOpen()) {
      this._setStickyShadeOpen(false);
      const toggle = this._stickyShadeRoot && this._stickyShadeRoot.querySelector('[data-phn-sticky-shade-toggle]');
      if (toggle) toggle.focus();
    }
  }

  _onStickyShadeItemClick(e) {
    e.preventDefault();
    const item = e.currentTarget;
    const variantId = item && item.getAttribute('data-variant-id');
    if (!variantId) return;
    // Close the dropdown first so the gallery isn't covered while the variant
    // swap animates the hero image in.
    this._setStickyShadeOpen(false);
    // Proxy to the corresponding primary-form swatch button. Its click handler
    // (`_onDelegatedClick → _selectSwatch`) handles input/URL/gallery/price
    // sync and our `_syncStickyShadeFromActive` updates the trigger button.
    const stdBtn = this.querySelector(`[data-phn-std-shade][data-variant-id="${variantId}"]`);
    if (stdBtn) stdBtn.click();
    this._syncStickyShadeFromActive();
  }

  /** Mirror the active primary swatch onto sticky shade label/swatch (desktop + mobile). */
  _syncStickyShadeFromActive() {
    const stickyRoot = this._getPhnSectionRoot()?.querySelector('[data-phn-sticky-root]');
    if (!stickyRoot) return;

    const active = this.querySelector('[data-phn-std-shade].is-active');
    if (!active) return;

    const variantId = active.getAttribute('data-variant-id');
    let variantTitle = active.getAttribute('data-variant-title') || active.getAttribute('aria-label') || '';
    const isNew =
      active.getAttribute('data-variant-is-new') === 'true' ||
      Boolean(active.querySelector('.phn-form-std__swatch-new'));
    const isUnavailable =
      active.classList.contains('is-unavailable') ||
      active.getAttribute('data-variant-available') === 'false';

    // Kit two-step picker (read-only sticky display): the label is the full
    // "Shade / Option 2" selection, not just the shade value, and the swatch
    // fill comes from the option-1 circle (`.product__form-swatch-circle`)
    // because the multi-option form has no `.phn-form-std__swatch-inner`.
    const isKitSticky = Boolean(stickyRoot.querySelector('[data-phn-sticky-shade-kit]'));
    let kitFillStyle = null;
    if (isKitSticky) {
      const activeSize = this.querySelector('[data-phn-std-size].is-active');
      const shadeName = active.getAttribute('data-shade-value') || variantTitle;
      const sizeName = activeSize ? activeSize.getAttribute('data-value') : '';
      variantTitle = sizeName ? `${shadeName} / ${sizeName}` : shadeName;

      const circleImg = active.querySelector('.product__form-swatch-circle img');
      const circleFill = active.querySelector('.product__form-swatch-circle i');
      if (circleImg && circleImg.getAttribute('src')) {
        kitFillStyle = `background-image:url("${circleImg.getAttribute('src')}");`;
      } else if (circleFill && circleFill.style.background) {
        kitFillStyle = `background-color:${circleFill.style.background};`;
      }
    }

    stickyRoot.querySelectorAll('[data-phn-sticky-shade-label]').forEach((labelEl) => {
      if (variantTitle) labelEl.textContent = variantTitle;
    });

    const innerEl = active.querySelector('.phn-form-std__swatch-inner');
    stickyRoot.querySelectorAll('[data-phn-sticky-shade-swatch]').forEach((swatchEl) => {
      swatchEl.classList.toggle('is-new', isNew);
      swatchEl.classList.toggle('is-unavailable', isUnavailable);
      const fillEl = swatchEl.querySelector('.product__sticky-shade-swatch-inner');
      if (!fillEl) return;
      if (kitFillStyle) {
        fillEl.setAttribute('style', kitFillStyle);
      } else if (innerEl) {
        fillEl.setAttribute('style', innerEl.getAttribute('style') || '');
      } else {
        const itemInner = stickyRoot.querySelector(
          `[data-phn-sticky-shade-item][data-variant-id="${variantId}"] .product__sticky-shade-item-swatch-inner`
        );
        if (itemInner) fillEl.setAttribute('style', itemInner.getAttribute('style') || '');
      }
    });

    const interactive = stickyRoot.querySelector('[data-phn-sticky-shade]');
    if (interactive) {
      interactive.querySelectorAll('[data-phn-sticky-shade-item]').forEach((item) => {
        const matches = item.getAttribute('data-variant-id') === variantId;
        const itemIsNew = item.getAttribute('data-variant-is-new') === 'true';
        const itemSwatch = item.querySelector('.product__sticky-shade-item-swatch');
        item.classList.toggle('is-active', matches);
        item.setAttribute('aria-selected', matches ? 'true' : 'false');
        if (itemSwatch) itemSwatch.classList.toggle('is-new', itemIsNew);
      });
    }
  }

  /* ─── Sticky scroll hooks (mini bar) ─────────────────────────────────────
   * Mirrors live `app/utilities/scrolling.js → onScrollHooks` while a
   * `.product__sticky` exists on the page:
   *   • `state--announcement-hidden` when `scrollTop > 0` — desktop mini bar
   *     show gate (paired with `state--selected-shades-full`).
   *   • `scrolling-up` on scroll direction — desktop mini bar hide on scroll up.
   *
   * Mobile mini bar only needs `state--selected-shades-full` (see CSS). The
   * announcement flag is still toggled here for parity with live but does not
   * gate mobile visibility.
   *
   * Scoped to hero JS (not the header) so standard sticky Batch 1 logic is
   * unaffected — rebuild has no `.scrolling-up` rules on `.product__sticky`.
   * ──────────────────────────────────────────────────────────────────────── */
  _initAnnouncementHiddenTrigger() {
    if (!document.querySelector('.product__sticky')) return; // matches live's SELECTOR_STICKY_ATC guard
    this._stickyPreviousScrollTop = window.scrollY || document.documentElement.scrollTop || 0;
    this._stickyScrollingUp = false;
    window.addEventListener('scroll', this._onStickyScrollHooks, { passive: true });
    document.addEventListener('mb-upsell-scroll-unlocked', this._onUpsellScrollUnlocked);
    // Initial sync — covers deep-link / page-reload scenarios where the user
    // lands mid-page (e.g. browser-restored scroll position).
    this._onStickyScrollHooks();
  }

  _destroyAnnouncementHiddenTrigger() {
    window.removeEventListener('scroll', this._onStickyScrollHooks);
    document.removeEventListener('mb-upsell-scroll-unlocked', this._onUpsellScrollUnlocked);
    document.body.classList.remove('state--announcement-hidden', 'scrolling-up');
    this._stickyPreviousScrollTop = 0;
    this._stickyScrollingUp = false;
  }

  _onUpsellScrollUnlocked(e) {
    const scrollY = e.detail?.scrollY ?? window.scrollY ?? 0;
    this._stickyPreviousScrollTop = scrollY <= 0 ? 0 : scrollY;
    this._stickyScrollingUp = false;
    document.body.classList.toggle('state--announcement-hidden', scrollY > 0);
    document.body.classList.remove('scrolling-up');
  }

  _onStickyScrollHooks() {
    if (document.documentElement.classList.contains('state--mb-upsell-open')) return;

    const scrollTop = window.scrollY || document.documentElement.scrollTop || 0;

    document.body.classList.toggle('state--announcement-hidden', scrollTop > 0);

    if (
      this._stickyPreviousScrollTop > scrollTop &&
      !this._stickyScrollingUp &&
      scrollTop > 0
    ) {
      document.body.classList.add('scrolling-up');
      this._stickyScrollingUp = true;
    } else if (this._stickyPreviousScrollTop < scrollTop && this._stickyScrollingUp) {
      document.body.classList.remove('scrolling-up');
      this._stickyScrollingUp = false;
    }

    this._stickyPreviousScrollTop = scrollTop <= 0 ? 0 : scrollTop;
  }
}

const onReviewsLinkClick = (e) => {
  const link = e.target.closest('a[href="#product-reviews"]');
  if (!link) return;
  const target = document.getElementById('product-reviews');
  if (!target) return;
  e.preventDefault();
  const headerH =
    parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 60;
  const top = target.getBoundingClientRect().top + window.scrollY - headerH - 8;
  window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
};

if (!window.__phnReviewsScrollBound) {
  window.__phnReviewsScrollBound = true;
  document.addEventListener('click', onReviewsLinkClick);
}

ProductHeroNew._multiPhnScrollBound = false;
ProductHeroNew._multiPhnScrollRaf = null;

if (!customElements.get('product-hero-new')) {
  customElements.define('product-hero-new', ProductHeroNew);
}

// Re-export for any consumer that still references the old helpers (not used internally).
window.JR_phn = { money: phnMoney, fixWidth: phnFixWidth };
